/**
 * Hobonichi launch — set builder step engine.
 *
 * Two halves, same shape as assets/hobonichi-quiz.js:
 *
 *   1. Pure navigation core (no DOM). A small, scoreless state machine over
 *      the five builder steps plus the shopper's selections. Loaded both by
 *      the browser AND by Node (module.exports) so the offline smoke test can
 *      assert the transitions without a DOM.
 *
 *   2. The browser UI, guarded behind `typeof document !== 'undefined'`, so
 *      requiring this file under Node never touches window/document. It wires
 *      the step-1 line cards + edition picker, the per-step cover / coc /
 *      extras grids, the running total, the review, and the single add-all
 *      request, persists to localStorage, maps browser back/forward onto the
 *      applicable steps via the history API, and reads the ?line= / ?book=
 *      pre-seeds.
 *
 * No dependencies. All shopper-visible strings are written via textContent.
 *
 * ---------------------------------------------------------------------------
 * Contract (element / data-attribute API this file drives) — kept stable
 * across T4 so any earlier integration keeps working:
 *   - Instance API, attached to the root element as `root.hobonichiBuilder`:
 *       getState()                       -> a clone of the current state
 *       goToStep(n)                      -> navigate (pushes a history entry)
 *       selectLine(key)                  -> set line (resets downstream)
 *       selectBook(variantId, meta)      -> set the chosen book
 *       selectCover(id) / selectCoc(id)  -> set cover / cover-on-cover
 *       toggleExtra(id)                  -> add/remove an extra
 *       pendingBook                      -> the ?book= handle (unresolved here)
 *   - DOM events dispatched on the root (bubbling), detail = {state, pendingBook}:
 *       hobonichi-builder:ready    once, after first paint
 *       hobonichi-builder:change   on any state change
 *       hobonichi-builder:step     on any step change
 *   - Mount points read from the server-rendered section:
 *       [data-builder-editions]                step-1 edition picker
 *       [data-builder-grid="cover|coc|extras"] per-step product grids
 *       [data-builder-review]                  step-5 line items
 *       [data-builder-total]                   running total
 *       [data-builder-quickview]               info modal <dialog> (card ⓘ opens it)
 *       [data-builder-addall]                  add-all button
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // -----------------------------------------------------------------------
  // 1. Pure navigation core (no DOM; also loaded under Node)
  // -----------------------------------------------------------------------

  var STEP_MIN = 1;
  var STEP_MAX = 5;
  var STATE_VERSION = 1;
  var STORAGE_KEY = 'hobonichi_builder_v1';
  var LINE_KEYS = ['original', 'planner', 'cousin', 'hon', 'weeks', 'dayfree', '5year'];

  function createState() {
    return {
      version: STATE_VERSION,
      step: STEP_MIN,
      line: null,
      book_variant_id: null,
      book_meta: null,
      cover_id: null,
      coc_id: null,
      extras: [],
      // Furthest step legitimately reached this session — grows with withStep,
      // hard-resets to STEP_MIN whenever withLine/withBook invalidate the
      // downstream selections (a different line or a different book). Drives
      // which step-indicator chips are navigable (see isStepReachable below).
      maxStepReached: STEP_MIN
    };
  }

  function cloneState(state) {
    return {
      version: STATE_VERSION,
      step: state.step,
      line: state.line,
      book_variant_id: state.book_variant_id,
      book_meta: state.book_meta,
      cover_id: state.cover_id,
      coc_id: state.coc_id,
      extras: state.extras ? state.extras.slice() : [],
      maxStepReached: typeof state.maxStepReached === 'number' ? state.maxStepReached : (state.step || STEP_MIN)
    };
  }

  function clampStep(n) {
    n = parseInt(n, 10);
    if (isNaN(n) || n < STEP_MIN) return STEP_MIN;
    if (n > STEP_MAX) return STEP_MAX;
    return n;
  }

  function isValidLine(key) {
    return LINE_KEYS.indexOf(key) !== -1;
  }

  function isId(value) {
    return typeof value === 'string' || typeof value === 'number';
  }

  // Cover-compatibility fits key for a book, from its line + physical size.
  // Mirrors snippets/hobonichi-fits-key.liquid exactly:
  //   5year -> 5year-<size>, hon -> hon-<size>, weeks -> weeks, else <size>.
  function fitsKeyForBook(line, size) {
    if (!line || !size) return '';
    if (line === '5year') return '5year-' + size;
    if (line === 'hon') return 'hon-' + size;
    if (size === 'weeks') return 'weeks';
    return size;
  }

  // Cover-on-cover only exists for the standard-cover lines. HON (cover built
  // in) and the 5-Year (own dedicated cover) never take a Cover on Cover, so
  // the coc step is skipped for them regardless of what's in stock.
  function isCocLineEligible(line) {
    return isValidLine(line) && line !== 'hon' && line !== '5year';
  }

  function withStep(state, n) {
    var next = cloneState(state);
    next.step = clampStep(n);
    // maxStepReached only ever grows here — a backward chip-jump (goToStep to
    // an earlier step) must not forget how far the shopper had gotten, or the
    // forward chips would re-lock the moment they looked back.
    next.maxStepReached = Math.max(next.maxStepReached, next.step);
    return next;
  }

  // Selecting a (different) line resets every downstream selection, since the
  // covers/coc/extras that fit change with the book. Re-selecting the same
  // line is a no-op clone. maxStepReached hard-resets too: whatever the
  // shopper reached for the OLD line no longer describes real progress once
  // its selections are wiped, so forward chips re-lock until re-earned.
  function withLine(state, key) {
    var next = cloneState(state);
    if (!isValidLine(key) || next.line === key) return next;
    next.line = key;
    next.book_variant_id = null;
    next.book_meta = null;
    next.cover_id = null;
    next.coc_id = null;
    next.extras = [];
    next.maxStepReached = STEP_MIN;
    return next;
  }

  // Picking a genuinely DIFFERENT book (not the first pick, not re-picking the
  // same one) resets maxStepReached the same way withLine does: the cover/coc/
  // extras steps ahead were validated against the old book and can't be
  // trusted to still apply, so their chips re-lock until re-earned.
  function withBook(state, variantId, meta) {
    var next = cloneState(state);
    var nextId = isId(variantId) ? variantId : null;
    var changedBook = state.book_variant_id != null && nextId != null && String(nextId) !== String(state.book_variant_id);
    next.book_variant_id = nextId;
    next.book_meta = meta != null ? meta : null;
    if (changedBook) next.maxStepReached = STEP_MIN;
    return next;
  }

  // Single source of truth for "can chip n be clicked right now". `flow` is the
  // caller's current applicable-steps array (browser: flowFor(state); Node
  // tests: a plain array) — kept as a parameter so this stays DOM-free.
  //   - the current step is never a nav target (rendered separately, inert)
  //   - a step outside the flow (coc skipped for HON/5-Year, etc.) never is
  //   - step 2+ always requires a book, regardless of what maxStepReached says
  //     (defends against a corrupted/hand-edited stored blob)
  //   - anything BEHIND the current step is always reachable
  //   - anything AHEAD of the current step is reachable up to maxStepReached
  function isStepReachable(state, n, flow) {
    n = clampStep(n);
    if (n === state.step) return false;
    if (!flow || flow.indexOf(n) === -1) return false;
    if (n > STEP_MIN && state.book_variant_id == null) return false;
    return n < state.step || n <= state.maxStepReached;
  }

  function withCover(state, id) {
    var next = cloneState(state);
    next.cover_id = isId(id) ? id : null;
    return next;
  }

  function withCoc(state, id) {
    var next = cloneState(state);
    next.coc_id = isId(id) ? id : null;
    return next;
  }

  function toggleExtra(state, id) {
    var next = cloneState(state);
    if (!isId(id)) return next;
    var at = next.extras.indexOf(id);
    if (at === -1) next.extras.push(id);
    else next.extras.splice(at, 1);
    return next;
  }

  // Reads ?line= and ?book= from a query string. Unknown lines are dropped;
  // the book handle is passed through verbatim (resolved to a variant in the
  // browser half against the rendered book cards).
  function parseParams(search) {
    var out = { line: null, book: null };
    if (typeof search !== 'string') return out;
    var query = search.charAt(0) === '?' ? search.slice(1) : search;
    if (!query) return out;
    query.split('&').forEach(function (pair) {
      if (!pair) return;
      var eq = pair.indexOf('=');
      var key = eq === -1 ? pair : pair.slice(0, eq);
      var raw = eq === -1 ? '' : pair.slice(eq + 1);
      var val;
      try {
        val = decodeURIComponent(raw.replace(/\+/g, ' '));
      } catch (error) {
        val = raw;
      }
      if (key === 'line') out.line = val;
      else if (key === 'book') out.book = val;
    });
    if (!isValidLine(out.line)) out.line = null;
    if (!out.book) out.book = null;
    return out;
  }

  function serialize(state) {
    return JSON.stringify(cloneState(state));
  }

  // Returns a normalised state, or null for anything unparseable / stale /
  // shape-wrong (poisoned storage, an older schema version). Callers fall back
  // to createState() on null.
  function deserialize(raw) {
    var obj;
    try {
      obj = JSON.parse(raw);
    } catch (error) {
      return null;
    }
    if (!obj || typeof obj !== 'object' || obj.version !== STATE_VERSION) return null;
    if (typeof obj.step !== 'number') return null;
    var next = createState();
    next.step = clampStep(obj.step);
    if (isValidLine(obj.line)) next.line = obj.line;
    if (isId(obj.book_variant_id)) next.book_variant_id = obj.book_variant_id;
    if (obj.book_meta && typeof obj.book_meta === 'object') next.book_meta = obj.book_meta;
    if (isId(obj.cover_id)) next.cover_id = obj.cover_id;
    if (isId(obj.coc_id)) next.coc_id = obj.coc_id;
    if (Array.isArray(obj.extras)) {
      next.extras = obj.extras.filter(isId);
    }
    // Graceful migration: a blob stored before maxStepReached existed simply
    // lacks the field — default it to the stored step (the shopper had at
    // least reached that far). A present-but-stale/tampered value is clamped
    // and floored at the restored step so it can never read as "reachable
    // less than where you already are".
    var storedMax = typeof obj.maxStepReached === 'number' ? clampStep(obj.maxStepReached) : next.step;
    next.maxStepReached = Math.max(storedMax, next.step);
    return next;
  }

  var core = {
    STEP_MIN: STEP_MIN,
    STEP_MAX: STEP_MAX,
    STATE_VERSION: STATE_VERSION,
    STORAGE_KEY: STORAGE_KEY,
    LINE_KEYS: LINE_KEYS,
    createState: createState,
    cloneState: cloneState,
    clampStep: clampStep,
    isValidLine: isValidLine,
    fitsKeyForBook: fitsKeyForBook,
    isCocLineEligible: isCocLineEligible,
    withStep: withStep,
    withLine: withLine,
    withBook: withBook,
    withCover: withCover,
    withCoc: withCoc,
    toggleExtra: toggleExtra,
    isStepReachable: isStepReachable,
    parseParams: parseParams,
    serialize: serialize,
    deserialize: deserialize
  };

  // -----------------------------------------------------------------------
  // 2. Browser UI (guarded so `require()` under Node never touches the DOM)
  // -----------------------------------------------------------------------

  if (typeof document !== 'undefined') {
    (function () {
      // GA4 event passthrough. snippets/ga4-tag.liquid defines gtag on every
      // storefront page; the guard keeps this file standalone (Node loading,
      // or the snippet ever removed) — without gtag, events just no-op.
      function track(name, params) {
        if (typeof gtag === 'function') gtag('event', name, params);
      }

      function readStored() {
        try {
          var raw = window.localStorage.getItem(STORAGE_KEY);
          return raw ? deserialize(raw) : null;
        } catch (error) {
          return null;
        }
      }

      function persist(state) {
        try {
          window.localStorage.setItem(STORAGE_KEY, serialize(state));
        } catch (error) {
          /* storage unavailable / full — the builder still works in-session */
        }
      }

      // Shipping-protection opt-out flag — the SAME localStorage key + semantics as
      // assets/cart-shipping-protection.js (key 'sp-opted-out'; '1' = opted out /
      // SP off, absent = default on). The builder reads it to reflect the review
      // toggle on render and writes it when the shopper flips that toggle, so
      // opt-out state is shared with the mini cart both ways through this one flag.
      // All access is guarded: blocked-storage privacy modes throw on any touch and
      // must degrade to "no opt-out recorded", never abort — mirrors the SP script,
      // including clearing the legacy sessionStorage twin on every write.
      var SP_OPT_OUT_KEY = 'sp-opted-out';
      function isSpOptedOut() {
        try {
          return Boolean(window.localStorage.getItem(SP_OPT_OUT_KEY));
        } catch (error) {
          return false;
        }
      }
      function setSpOptedOut(value) {
        try {
          if (value) window.localStorage.setItem(SP_OPT_OUT_KEY, '1');
          else window.localStorage.removeItem(SP_OPT_OUT_KEY);
          try {
            window.sessionStorage.removeItem(SP_OPT_OUT_KEY);
          } catch (ignored) {
            /* sessionStorage blocked — nothing to migrate away */
          }
        } catch (error) {
          /* storage blocked — the review toggle still works in-session */
        }
      }

      // Canonical Shopify money formatter, driven by shop.money_format so the
      // computed total matches the per-item `| money` output exactly.
      function makeMoney(format) {
        var placeholder = /\{\{\s*(\w+)\s*\}\}/;
        function fmt(number, precision, thousands, decimal) {
          precision = precision == null ? 2 : precision;
          thousands = thousands == null ? ',' : thousands;
          decimal = decimal == null ? '.' : decimal;
          if (isNaN(number) || number == null) return '0';
          number = (Number(number) / 100).toFixed(precision);
          var parts = number.split('.');
          var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
          var rest = parts[1] ? decimal + parts[1] : '';
          return dollars + rest;
        }
        return function (cents) {
          var f = format || '${{amount}}';
          var m = f.match(placeholder);
          if (!m) return f;
          var value;
          switch (m[1]) {
            case 'amount': value = fmt(cents, 2); break;
            case 'amount_no_decimals': value = fmt(cents, 0); break;
            case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
            case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
            case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
            case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
            default: value = fmt(cents, 2);
          }
          return f.replace(placeholder, value);
        };
      }

      function el(tag, className) {
        var node = document.createElement(tag);
        if (className) node.className = className;
        return node;
      }

      function prefersReducedMotion() {
        return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      }

      // Pull a freshly-revealed region to the top of the viewport and move focus
      // into it, without letting focus() fire its own competing jump-scroll
      // (preventScroll + one smooth scroll instead of two fighting ones). Used
      // for the intra-step line -> edition-picker reveal, which on a phone lands
      // a screen or more below the line cards and otherwise looks like nothing
      // happened. Honours reduce-motion.
      function revealRegion(scrollTarget, focusTarget) {
        if (!scrollTarget) return;
        try {
          scrollTarget.scrollIntoView({
            block: 'start',
            behavior: prefersReducedMotion() ? 'auto' : 'smooth'
          });
        } catch (error) {
          try { scrollTarget.scrollIntoView(); } catch (ignored) { /* no-op */ }
        }
        if (focusTarget && typeof focusTarget.focus === 'function') {
          try {
            focusTarget.focus({ preventScroll: true });
          } catch (error) {
            try { focusTarget.focus(); } catch (ignored) { /* no-op */ }
          }
        }
      }

      function init(root) {
        var params = parseParams(window.location.search);
        var state = readStored() || createState();

        // Pre-seeds arriving from the quiz / a PDP take precedence over any
        // stale stored session. ?line= lands on step 1 (the edition picker);
        // ?book= is remembered for resolution against the rendered book cards.
        if (params.line) {
          state = withStep(withLine(state, params.line), 1);
        }
        var pendingBook = params.book;
        if (pendingBook) root.setAttribute('data-pending-book', pendingBook);

        var money = makeMoney(root.getAttribute('data-money-format'));
        var copy = {
          book: root.getAttribute('data-label-book') || 'Book',
          cover: root.getAttribute('data-label-cover') || 'Cover',
          coc: root.getAttribute('data-label-coc') || 'Cover on cover',
          extra: root.getAttribute('data-label-extra') || 'Extra',
          change: root.getAttribute('data-copy-change') || 'Change',
          adding: root.getAttribute('data-copy-adding') || 'Preparing checkout',
          added: root.getAttribute('data-copy-added') || 'Added to cart. Opening checkout.',
          error: root.getAttribute('data-copy-error') || 'Something went wrong. Please try again.',
          soldoutRetry: root.getAttribute('data-copy-soldout') || 'This item just sold out. Choose another to continue.',
          freeReached: root.getAttribute('data-copy-free-qualify') || 'You qualify!',
          freeAway: root.getAttribute('data-copy-free-away') || 'away',
          freeCart: root.getAttribute('data-copy-free-cart') || 'Your cart already qualifies for free shipping.',
          freeAnnounce: root.getAttribute('data-copy-free-announce') || 'You now qualify for free shipping.',
          promoBelow: root.getAttribute('data-copy-promo-below') || 'Hobonichi items only. Spend $120 for a free planner page clip. Reach $200 to add the journal shoulder pouch as well. While stocks last.',
          promoT1Status: root.getAttribute('data-copy-promo-t1-status') || 'Page clip unlocked',
          promoMid: root.getAttribute('data-copy-promo-mid') || '{amount} more on Hobonichi items adds the free journal shoulder pouch.',
          promoT2Status: root.getAttribute('data-copy-promo-t2-status') || 'Both gifts unlocked',
          promoT2Caption: root.getAttribute('data-copy-promo-t2-caption') || 'Both gifts unlocked. Choose them from the gift offer before checkout.',
          promoCartT1: root.getAttribute('data-copy-promo-cart-t1') || 'Your cart already qualifies for the page clip.',
          promoCartT2: root.getAttribute('data-copy-promo-cart-t2') || 'Your cart already qualifies for both gifts.',
          promoAnnounceT1: root.getAttribute('data-copy-promo-announce-t1') || 'You have unlocked the free planner page clip.',
          promoAnnounceT2: root.getAttribute('data-copy-promo-announce-t2') || 'You have unlocked the free journal shoulder pouch.',
          promoNudgeT1: root.getAttribute('data-copy-promo-nudge-t1') || 'Add {amount} in extras to unlock the free page clip.',
          promoNudgeT2: root.getAttribute('data-copy-promo-nudge-t2') || 'Add {amount} in extras to unlock the free shoulder pouch.',
          promoNudgeDone: root.getAttribute('data-copy-promo-nudge-done') || 'Your set qualifies for both free Hobonichi gifts.',
          promoClipSoldout: root.getAttribute('data-copy-promo-clip-soldout') || 'The page clip has sold out.',
          promoPouchSoldout: root.getAttribute('data-copy-promo-pouch-soldout') || 'The journal pouch has sold out.',
          skip: root.getAttribute('data-copy-skip') || 'Skip',
          skipCover: root.getAttribute('data-copy-skip-cover') || 'Skip covers, no cover for this set',
          skipCoc: root.getAttribute('data-copy-skip-coc') || 'Skip cover on cover, none for this set',
          skipExtra: root.getAttribute('data-copy-skip-extra') || 'Skip extras, no extras for this set',
          editionsPrompt: root.getAttribute('data-copy-editions-prompt') || 'Pick a line above to see the books that fit.',
          editionsEmpty: root.getAttribute('data-copy-editions-empty') || 'No books in this line yet.',
          itemMax: root.getAttribute('data-copy-item-max') || 'Maximum quantity already in your cart',
          itemAdded: root.getAttribute('data-copy-item-added') || 'Added',
          partialHeading: root.getAttribute('data-copy-partial-heading') || 'Some items could not be added',
          reportCheckout: root.getAttribute('data-copy-report-checkout') || 'Continue to checkout'
        };
        var freeThreshold = parseInt(root.getAttribute('data-free-threshold'), 10);
        if (isNaN(freeThreshold)) freeThreshold = 0;

        // Hobonichi gift-with-purchase bar (promo live only; council wf_41743294-45c).
        // Thresholds arrive as data attributes but are fixed in the section's Liquid
        // ($120/$200) to match the gift app's config. The builder set counts fully
        // (every selectable product is vendor Hobonichi); the live cart contributes
        // only its Hobonichi-vendor lines, minus the gift products themselves.
        var promoT1 = parseInt(root.getAttribute('data-promo-t1'), 10) || 0;
        var promoT2 = parseInt(root.getAttribute('data-promo-t2'), 10) || 0;
        var promoClipAvail = root.getAttribute('data-promo-clip-available') === 'true';
        var promoPouchAvail = root.getAttribute('data-promo-pouch-available') === 'true';
        var promoClipId = parseInt(root.getAttribute('data-promo-clip-id'), 10) || 0;
        var promoPouchId = parseInt(root.getAttribute('data-promo-pouch-id'), 10) || 0;

        // Card-level labels the client-built (past-the-50-cap) extras cards need to
        // read back so they render identically to the server ones. Defaults mirror
        // the section's Liquid defaults.
        var soldoutLabel = root.getAttribute('data-soldout-label') || 'Sold out';
        var infoLabel = root.getAttribute('data-info-label') || 'View full details for';
        var newtabLabel = root.getAttribute('data-newtab-label') || 'opens in a new tab';
        var extrasLoadingCopy = root.getAttribute('data-copy-extras-loading') || 'Loading more extras…';
        var extrasErrorCopy =
          root.getAttribute('data-copy-extras-error') || 'Some extras could not be loaded. Refresh to try again.';

        var panels = {};
        root.querySelectorAll('[data-builder-step]').forEach(function (node) {
          panels[node.getAttribute('data-builder-step')] = node;
        });
        var indicators = {};
        root.querySelectorAll('[data-builder-step-indicator]').forEach(function (node) {
          indicators[node.getAttribute('data-builder-step-indicator')] = node;
        });

        var progressText = root.querySelector('[data-builder-progress-text]');
        var backBtn = root.querySelector('[data-builder-back]');
        var nextBtn = root.querySelector('[data-builder-next]');
        // Skip lives in the action bar now (not in-content); shown by renderButtons
        // only on the optional steps. Reuses the [data-builder-skip] click delegation.
        var navSkipBtn = root.querySelector('[data-builder-nav-skip]');
        var nextBaseLabel = nextBtn ? ((nextBtn.textContent || '').trim() || 'Next') : 'Next';
        var addAllBtn = root.querySelector('[data-builder-addall]');
        var totalEl = root.querySelector('[data-builder-total]');
        var reviewMount = root.querySelector('[data-builder-review]');
        var reviewTotalEl = root.querySelector('[data-builder-review-total]');
        var freeShipEl = root.querySelector('[data-builder-freeship]');
        var freeBarEl = root.querySelector('[data-builder-freeship-bar]');
        var freeFillEl = root.querySelector('[data-builder-freeship-fill]');
        var freeStatusEl = root.querySelector('[data-builder-freeship-status]');
        var freeNoteEl = root.querySelector('[data-builder-freeship-note]');
        var freeLiveEl = root.querySelector('[data-builder-freeship-live]');
        var giftBarWrap = root.querySelector('[data-builder-giftbar]');
        var giftBarBar = root.querySelector('[data-builder-giftbar-bar]');
        var giftBarFill = root.querySelector('[data-builder-giftbar-fill]');
        var giftBarStatus = root.querySelector('[data-builder-giftbar-status]');
        var giftBarCaption = root.querySelector('[data-builder-giftbar-caption]');
        var giftBarNote = root.querySelector('[data-builder-giftbar-note]');
        var giftBarLive = root.querySelector('[data-builder-giftbar-live]');
        var giftBarDot = root.querySelector('[data-builder-giftbar-dot]');
        var promoNudgeEl = root.querySelector('[data-builder-promo-nudge]');
        var addErrorEl = root.querySelector('[data-builder-addall-error]');
        var addReportEl = root.querySelector('[data-builder-addall-report]');
        var addStatusEl = root.querySelector('[data-builder-addall-status]');

        // Shipping-protection module (review step). Builder-owned and inert toward
        // the cart: the toggle carries no name="attributes[shipping-protection]" and
        // no data-variantid, so cart-shipping-protection.js never binds to it — the
        // builder alone drives it and the shared opt-out flag (above). spModule is
        // absent for B2B, or when the shipping-protection linklist has no variant
        // (both handled in the section), in which case SP folds out of every total.
        var spModule = root.querySelector('[data-builder-sp]');
        var spToggle = root.querySelector('[data-builder-sp-toggle]');
        var spLineEl = root.querySelector('[data-builder-sp-line]');
        var spLineAmountEl = root.querySelector('[data-builder-sp-line-amount]');
        var spPriceCents = spModule ? parseInt(spModule.getAttribute('data-sp-price'), 10) || 0 : 0;
        var spPriceText = spModule ? spModule.getAttribute('data-sp-price-text') || '' : '';
        // The SP variant id (resolved server-side from the shipping-protection linklist,
        // the same source as the price). Lets the review toggle actively add/remove the
        // real cart SP line so the mini cart stays in lockstep — see the two-way sync
        // wiring further down. Absent for B2B (no SP module) — every SP path guards on it.
        var spVariantId = spModule ? spModule.getAttribute('data-sp-variant') || '' : '';

        var editionsMount = root.querySelector('[data-builder-editions]');
        var editionsNote = root.querySelector('[data-editions-note]');
        var coverGuidance = root.querySelector('[data-cover-guidance]');
        var coverGuidanceHon = root.querySelector('[data-cover-guidance-hon]');

        var lineButtons = Array.prototype.slice.call(root.querySelectorAll('[data-builder-line]'));
        var bookCards = Array.prototype.slice.call(
          editionsMount ? editionsMount.querySelectorAll('[data-builder-card][data-role="book"]') : []
        );

        // Build a single lookup for every rendered card (book + cover + coc +
        // extras). The review + running total read from here by variant id, so
        // the engine state only ever needs to carry ids.
        var cardIndex = {};
        root.querySelectorAll('[data-builder-card]').forEach(function (card) {
          var id = card.getAttribute('data-variant-id');
          if (!id) return;
          cardIndex[String(id)] = {
            role: card.getAttribute('data-role') || '',
            title: card.getAttribute('data-title') || '',
            price: parseInt(card.getAttribute('data-price'), 10) || 0,
            priceText: card.getAttribute('data-price-text') || '',
            image: card.getAttribute('data-image') || '',
            handle: card.getAttribute('data-handle') || '',
            line: card.getAttribute('data-line') || '',
            size: card.getAttribute('data-size') || '',
            available: card.getAttribute('data-available') === 'true'
          };
        });

        var submitting = false;
        // Straight-to-checkout state (final step): once a full add-all succeeds we
        // go to checkout via an SP-gated real cart-form submit, so keep the button
        // in its "preparing" state (navigating) and never fire the submit twice
        // (checkoutSubmitted covers both the full-success path and the partial
        // report's "Continue to checkout" action).
        var cartUrl = root.getAttribute('data-cart-url') || '/cart';
        var navigating = false;
        var checkoutSubmitted = false;

        function lookup(id) {
          return id != null ? cardIndex[String(id)] || null : null;
        }

        function selectedBook() {
          return lookup(state.book_variant_id);
        }

        // Line / size / cover-fits-key resolved for an explicit state, so the flow
        // can be computed for a *pending* selection (e.g. the book just picked)
        // before it is committed. The no-arg wrappers below read the live state.
        function bookLineFor(s) {
          var b = lookup(s.book_variant_id);
          return s.line || (b && b.line) || '';
        }

        function bookSizeFor(s) {
          var b = lookup(s.book_variant_id);
          return (b && b.size) || (s.book_meta && s.book_meta.size) || '';
        }

        function coverFitsKeyFor(s) {
          return fitsKeyForBook(bookLineFor(s), bookSizeFor(s));
        }

        function bookLine() { return bookLineFor(state); }
        function bookSize() { return bookSizeFor(state); }
        function coverFitsKey() { return coverFitsKeyFor(state); }

        // Count the available cards in a keyed grid, so an empty grid auto-skips
        // its step. Symmetric for the cover fits-key grids and the coc size grids.
        function coverAvailableCount(key) {
          if (!key) return 0;
          return root.querySelectorAll(
            '[data-fits-key="' + key + '"] [data-builder-card][data-available="true"]'
          ).length;
        }

        function cocAvailableCount(size) {
          if (!size) return 0;
          return root.querySelectorAll(
            '[data-coc-size="' + size + '"] [data-builder-card][data-available="true"]'
          ).length;
        }

        // Step 2 (Cover) applies unless a *chosen* book's fitting grid is provably
        // empty (e.g. 5-Year A5 with no covers in stock) — then it auto-skips, the
        // same way coc does. Before a book is chosen we keep it in the flow.
        function coverApplicableFor(s) {
          if (s.book_variant_id == null) return true;
          return coverAvailableCount(coverFitsKeyFor(s)) > 0;
        }

        function cocApplicableFor(s) {
          return isCocLineEligible(bookLineFor(s)) && cocAvailableCount(bookSizeFor(s)) > 0;
        }

        function coverApplicable() { return coverApplicableFor(state); }
        function cocApplicable() { return cocApplicableFor(state); }

        // The ordered list of steps that apply to a given state's selection.
        function flowFor(s) {
          var steps = [1];
          if (coverApplicableFor(s)) steps.push(2);
          if (cocApplicableFor(s)) steps.push(3);
          steps.push(4, 5);
          return steps;
        }

        function flow() { return flowFor(state); }

        // First applicable step after the book step for a (pending) selection —
        // used when a book is picked so we land on Cover, or skip straight to Coc /
        // Extras when that book has no fitting covers.
        function firstStepAfterBook(s) {
          var f = flowFor(s);
          var i = f.indexOf(1);
          return f[Math.min(i + 1, f.length - 1)];
        }

        // Snap a desired step onto the applicable flow (used when history /
        // restore lands on a step that no longer applies to this book).
        function snapStep(desired) {
          var f = flow();
          if (f.indexOf(desired) !== -1) return desired;
          var best = f[0];
          for (var i = 0; i < f.length; i++) {
            if (f[i] <= desired) best = f[i];
          }
          return best;
        }

        function nextStep() {
          var f = flow();
          var i = f.indexOf(snapStep(state.step));
          return f[Math.min(i + 1, f.length - 1)];
        }

        function prevStep() {
          var f = flow();
          var i = f.indexOf(snapStep(state.step));
          return f[Math.max(i - 1, 0)];
        }

        function totalCents() {
          var sum = 0;
          var book = selectedBook();
          if (book) sum += book.price;
          var cover = lookup(state.cover_id);
          if (cover) sum += cover.price;
          var coc = lookup(state.coc_id);
          if (coc) sum += coc.price;
          state.extras.forEach(function (id) {
            var e = lookup(id);
            if (e) sum += e.price;
          });
          return sum;
        }

        // SP price in cents when shipping protection is ACTIVE for this set:
        //   - resolved (the linklist yielded a variant -> spPriceCents > 0),
        //   - a real product is in the set (a book is chosen) — SP never rides an
        //     empty set, mirroring the cart where SP only exists alongside items,
        //   - the shopper has not opted out (shared flag, read fresh every call so a
        //     change made in the mini cart or another tab is picked up on re-render).
        // 0 otherwise. B2B never reaches here — the section renders no SP module.
        function spLineCents() {
          if (spPriceCents <= 0) return 0;
          if (state.book_variant_id == null) return 0;
          if (isSpOptedOut()) return 0;
          return spPriceCents;
        }

        // The total shown on the REVIEW step and charged at checkout: the product
        // running total plus SP when active. Kept separate from totalCents() (which
        // stays product-only for the pre-review sticky bar) so SP is folded in only
        // where its toggle is visible — exactly as the cart reflects SP in
        // cart.total_price alongside its visible SP toggle, not before.
        function reviewTotalCents() {
          return totalCents() + spLineCents();
        }

        // ---- live cart awareness (free-shipping progress bar) ---------------
        //
        // The header's free-shipping bar (visible on every step) counts what's ALREADY
        // in the shopper's cart (from /cart.js) PLUS this in-progress set, against
        // settings.free_shipping_threshold.
        // liveCartCents is that existing-cart total in cents, EXCLUDING any shipping-
        // protection line: SP is counted exactly once, from the set side (reviewTotalCents),
        // so a reconciled cart SP line can never double-count. freeReachedState tracks the
        // qualify threshold so we announce only on an actual crossing.
        var liveCartCents = 0;
        var freeReachedState = null;
        // Gift-bar state: Hobonichi-vendor cents in the live cart, and the last
        // rendered tier (null on first paint so announcements only fire on real
        // upward crossings, mirroring freeReachedState).
        var liveHoboCents = 0;
        var giftTierState = null;

        function cartTotalExclSp(cart) {
          var total = cart && typeof cart.total_price === 'number' ? cart.total_price : 0;
          var idNum = spVariantIdNum();
          (cart && cart.items ? cart.items : []).forEach(function (item) {
            if (item.id === idNum) {
              var line = typeof item.final_line_price === 'number'
                ? item.final_line_price
                : (typeof item.line_price === 'number' ? item.line_price : 0);
              total -= line;
            }
          });
          return total < 0 ? 0 : total;
        }

        // Cheap single GET of the live cart; refreshes the bar on success. No-op when
        // there is no bar (threshold 0 / B2B). Failure leaves the set-only math showing.
        function refreshLiveCart() {
          // Fetch when EITHER bar needs live-cart data: the shipping bar
          // (threshold > 0) or the gift bar (mount present at promo live).
          if (freeThreshold <= 0 && !giftBarWrap) return;
          fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function (res) { return res.ok ? res.json() : null; })
            .then(function (cart) {
              if (!cart) return;
              liveCartCents = cartTotalExclSp(cart);
              liveHoboCents = cartHoboCents(cart);
              renderFreeShip();
            })
            .catch(function () {
              /* offline / blocked — the set-only bar stays as a best effort */
            });
        }

        // Hobonichi-vendor spend already in the live cart, excluding the gift
        // products themselves (a $0 or discounted gift line must not feed the
        // ladder that earned it). Case-insensitive vendor match, same
        // final_line_price fallback chain as cartTotalExclSp.
        function cartHoboCents(cart) {
          var sum = 0;
          (cart && cart.items ? cart.items : []).forEach(function (item) {
            if (promoClipId && item.product_id === promoClipId) return;
            if (promoPouchId && item.product_id === promoPouchId) return;
            var vendor = (item.vendor || '').toLowerCase();
            if (vendor !== 'hobonichi') return;
            var line = typeof item.final_line_price === 'number'
              ? item.final_line_price
              : (typeof item.line_price === 'number' ? item.line_price : 0);
            sum += line;
          });
          return sum;
        }

        function selectedIds() {
          var ids = [];
          if (state.book_variant_id != null) ids.push(state.book_variant_id);
          if (state.cover_id != null) ids.push(state.cover_id);
          if (state.coc_id != null) ids.push(state.coc_id);
          state.extras.forEach(function (id) { ids.push(id); });
          return ids;
        }

        function hasSoldOutSelection() {
          return selectedIds().some(function (id) {
            var entry = lookup(id);
            return entry && entry.available === false;
          });
        }

        // ---- rendering ------------------------------------------------------

        function renderEditions() {
          if (!editionsMount) return;
          var line = state.line;
          var hasLine = !!line;
          if (editionsNote) {
            if (!hasLine) {
              editionsNote.textContent = copy.editionsPrompt;
            } else {
              var visible = 0;
              bookCards.forEach(function (card) {
                if (card.getAttribute('data-line') === line) visible++;
              });
              var name = '';
              var btn = lineButtons.filter(function (b) {
                return b.getAttribute('data-builder-line') === line;
              })[0];
              if (btn) {
                var nameEl = btn.querySelector('.hobonichi-builder-app__line-name');
                if (nameEl) name = nameEl.textContent.trim();
              }
              editionsNote.textContent = visible > 0
                ? (name ? name + ': choose your book' : 'Choose your book')
                : copy.editionsEmpty;
            }
          }
          bookCards.forEach(function (card) {
            var show = hasLine && card.getAttribute('data-line') === line;
            card.hidden = !show;
          });
        }

        function renderCoverGrids() {
          var key = coverFitsKey();
          root.querySelectorAll('[data-fits-key]').forEach(function (grid) {
            grid.hidden = grid.getAttribute('data-fits-key') !== key;
          });
          var isHon = bookLine() === 'hon';
          if (coverGuidance) coverGuidance.hidden = isHon;
          if (coverGuidanceHon) coverGuidanceHon.hidden = !isHon;
        }

        function renderCocGrids() {
          var size = bookSize();
          root.querySelectorAll('[data-coc-size]').forEach(function (grid) {
            grid.hidden = grid.getAttribute('data-coc-size') !== size;
          });
        }

        function renderExtrasGrids() {
          var size = bookSize();
          root.querySelectorAll('[data-extras-size]').forEach(function (grid) {
            grid.hidden = grid.getAttribute('data-extras-size') !== size;
          });
          // The universal grid is shown for every line — leave it visible.
        }

        function renderCardSelection() {
          root.querySelectorAll('[data-builder-card]').forEach(function (card) {
            var id = card.getAttribute('data-variant-id');
            var role = card.getAttribute('data-role');
            var selected = false;
            if (role === 'book') selected = String(state.book_variant_id) === id;
            else if (role === 'cover') selected = String(state.cover_id) === id;
            else if (role === 'coc') selected = String(state.coc_id) === id;
            else if (role === 'extra') selected = state.extras.map(String).indexOf(id) !== -1;
            card.classList.toggle('hb-card--selected', selected);
            var pick = card.querySelector('[data-builder-pick]');
            if (pick) pick.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
        }

        function reviewRow(labelText, entry, gotoStep) {
          var row = el('div', 'hb-review__row');
          if (entry.available === false) row.classList.add('hb-review__row--soldout');

          var media = el('div', 'hb-review__media');
          if (entry.image) {
            var img = document.createElement('img');
            img.src = entry.image;
            img.alt = '';
            img.loading = 'lazy';
            img.className = 'hb-review__img';
            media.appendChild(img);
          }
          row.appendChild(media);

          var body = el('div', 'hb-review__body');
          var eyebrow = el('span', 'hb-review__eyebrow');
          eyebrow.textContent = labelText;
          body.appendChild(eyebrow);
          var title = el('span', 'hb-review__title');
          title.textContent = entry.title;
          body.appendChild(title);
          if (entry.available === false) {
            var note = el('span', 'hb-review__soldout');
            note.textContent = copy.soldoutRetry;
            body.appendChild(note);
          }
          row.appendChild(body);

          var side = el('div', 'hb-review__side');
          var price = el('span', 'hb-review__price');
          price.textContent = entry.priceText || money(entry.price);
          side.appendChild(price);
          var change = el('button', 'hb-review__change link');
          change.type = 'button';
          change.textContent = copy.change;
          change.setAttribute('data-review-change', String(gotoStep));
          side.appendChild(change);
          row.appendChild(side);

          return row;
        }

        function renderReview() {
          if (!reviewMount) return;
          reviewMount.textContent = '';
          var frag = document.createDocumentFragment();
          var book = selectedBook();
          if (book) frag.appendChild(reviewRow(copy.book, book, 1));
          var cover = lookup(state.cover_id);
          if (cover) frag.appendChild(reviewRow(copy.cover, cover, 2));
          var coc = lookup(state.coc_id);
          if (coc) frag.appendChild(reviewRow(copy.coc, coc, 3));
          state.extras.forEach(function (id) {
            var e = lookup(id);
            if (e) frag.appendChild(reviewRow(copy.extra, e, 4));
          });
          reviewMount.appendChild(frag);
        }

        function renderTotal() {
          // Sticky "Your set" bar shows the product-only running total on the build
          // steps, then the SP-inclusive review total once on the review step, so the
          // two totals visible on that screen always agree (and match what checkout
          // charges). The review total element always reflects SP.
          var reviewText = money(reviewTotalCents());
          if (totalEl) totalEl.textContent = state.step === STEP_MAX ? reviewText : money(totalCents());
          if (reviewTotalEl) reviewTotalEl.textContent = reviewText;
        }

        // Reflect the shared opt-out flag on the review toggle and show/hide the SP
        // charge line to match. Read fresh so an opt-out made in the mini cart (or
        // another tab) is honoured whenever the review re-renders.
        function renderSp() {
          if (!spModule) return;
          if (spToggle) spToggle.checked = !isSpOptedOut();
          if (spLineAmountEl) spLineAmountEl.textContent = spPriceText || money(spPriceCents);
          if (spLineEl) spLineEl.hidden = spLineCents() <= 0;
        }

        // Gift bar — sibling of renderFreeShip, driven from its every call site via
        // the hook at the top of renderFreeShip (so any event that can move either
        // total repaints both bars). No-ops when the mount is absent (promo off,
        // B2B, or both gifts sold out server-side).
        function renderGiftBar() {
          if (!giftBarWrap) return;
          // The set is 100% Hobonichi spend by construction; add the live cart's
          // Hobonichi-vendor lines. SP is excluded on both sides (not vendor
          // Hobonichi; set math is product-only).
          var combined = totalCents() + liveHoboCents;
          if (combined <= 0) {
            giftBarWrap.hidden = true;
            if (promoNudgeEl) promoNudgeEl.hidden = true;
            return;
          }
          giftBarWrap.hidden = false;

          // Track shape by remaining gifts (mirrors the drawer): both -> $200
          // track with the $120 dot; pouch gone -> $120 track, no dot; clip
          // gone -> $200 track, no dot.
          var bothAvail = promoClipAvail && promoPouchAvail;
          var trackMax = promoPouchAvail ? promoT2 : promoT1;
          if (giftBarDot) giftBarDot.hidden = !bothAvail;

          var pct = Math.round((combined * 100) / trackMax);
          if (pct > 100) pct = 100;
          if (pct < 0) pct = 0;
          if (giftBarFill) giftBarFill.style.width = pct + '%';
          if (giftBarDot && bothAvail) {
            giftBarDot.classList.toggle(
              'hobonichi-builder-app__giftbar-milestone--reached',
              combined >= promoT1
            );
          }

          // Effective tier for state + announcements: 0 none, 1 clip, 2 pouch
          // (or the single remaining gift when one has sold out).
          var tier = 0;
          var statusText = '';
          var captionText = '';
          var nudgeText = '';
          if (bothAvail) {
            if (combined >= promoT2) {
              tier = 2;
              statusText = copy.promoT2Status;
              captionText = copy.promoT2Caption;
              nudgeText = copy.promoNudgeDone;
            } else if (combined >= promoT1) {
              tier = 1;
              statusText = copy.promoT1Status;
              captionText = copy.promoMid.replace('{amount}', money(promoT2 - combined));
              nudgeText = copy.promoNudgeT2.replace('{amount}', money(promoT2 - combined));
            } else {
              statusText = money(promoT1 - combined) + ' ' + copy.freeAway;
              captionText = copy.promoBelow;
              nudgeText = copy.promoNudgeT1.replace('{amount}', money(promoT1 - combined));
            }
          } else if (promoPouchAvail) {
            // Clip sold out: the pouch tier is the only target.
            if (combined >= promoT2) {
              tier = 2;
              statusText = copy.promoT2Status;
              captionText = copy.promoT2Caption;
              nudgeText = 'Your set qualifies for the free shoulder pouch.';
            } else {
              statusText = money(promoT2 - combined) + ' ' + copy.freeAway;
              captionText = copy.promoBelow;
              nudgeText = copy.promoNudgeT2.replace('{amount}', money(promoT2 - combined));
            }
          } else {
            // Pouch sold out: the clip tier caps the track.
            if (combined >= promoT1) {
              tier = 1;
              statusText = copy.promoT1Status;
              captionText = copy.promoPouchSoldout + ' Page clip unlocked. Choose it from the gift offer before checkout.';
              nudgeText = 'Your set qualifies for the free page clip.';
            } else {
              statusText = money(promoT1 - combined) + ' ' + copy.freeAway;
              captionText = copy.promoPouchSoldout + ' Spend $120 on Hobonichi items for the free planner page clip. While stocks last.';
              nudgeText = copy.promoNudgeT1.replace('{amount}', money(promoT1 - combined));
            }
          }

          if (giftBarStatus) {
            giftBarStatus.textContent = statusText;
            giftBarStatus.classList.toggle('hobonichi-builder-app__freeship-status--qualified', tier > 0);
          }
          if (giftBarCaption) giftBarCaption.textContent = captionText;
          if (giftBarBar) {
            giftBarBar.setAttribute('aria-valuemax', String(trackMax));
            giftBarBar.setAttribute('aria-valuenow', String(Math.min(combined, trackMax)));
            giftBarBar.setAttribute('aria-valuetext', captionText);
          }

          // Honesty note — when the EXISTING cart alone already clears a tier,
          // say so rather than implying this (possibly empty) set earned it.
          if (giftBarNote) {
            if (promoPouchAvail && liveHoboCents >= promoT2) {
              giftBarNote.textContent = bothAvail ? copy.promoCartT2 : 'Your cart already qualifies for the shoulder pouch.';
              giftBarNote.hidden = false;
            } else if (promoClipAvail && liveHoboCents >= promoT1) {
              giftBarNote.textContent = copy.promoCartT1;
              giftBarNote.hidden = false;
            } else {
              giftBarNote.textContent = '';
              giftBarNote.hidden = true;
            }
          }

          // Review-step nudge: the line only renders inside the review panel, so
          // writing it every repaint is safe; visibility follows the bar.
          if (promoNudgeEl) {
            promoNudgeEl.textContent = nudgeText;
            promoNudgeEl.hidden = nudgeText === '';
          }

          // Polite announcement on an upward tier crossing only — never first
          // paint, never downward (removals speak through the visible bar).
          if (giftTierState !== null && tier > giftTierState && giftBarLive) {
            giftBarLive.textContent = tier === 2 && promoPouchAvail
              ? copy.promoAnnounceT2
              : copy.promoAnnounceT1;
          }
          giftTierState = tier;
        }

        function renderFreeShip() {
          renderGiftBar();
          if (!freeShipEl || freeThreshold <= 0) return;
          // Existing cart (excl SP) + this set (SP folded in once when on), vs the
          // same threshold the cart's own bar (snippets/shipping-progress.liquid) uses.
          var setCents = reviewTotalCents();
          var cartCents = liveCartCents;
          var combined = cartCents + setCents;
          // Empty cart AND empty set: keep the bar out of sight rather than show a
          // 0% / "$149 away" bar on an empty review.
          if (combined <= 0) {
            freeShipEl.hidden = true;
            return;
          }
          freeShipEl.hidden = false;

          var reached = combined >= freeThreshold;
          var pct = Math.round((combined * 100) / freeThreshold);
          if (pct > 100) pct = 100;
          if (pct < 0) pct = 0;
          if (freeFillEl) freeFillEl.style.width = pct + '%';

          var statusText = reached
            ? copy.freeReached
            : money(freeThreshold - combined) + ' ' + copy.freeAway;
          if (freeStatusEl) {
            freeStatusEl.textContent = statusText;
            freeStatusEl.classList.toggle('hobonichi-builder-app__freeship-status--qualified', reached);
          }
          if (freeBarEl) {
            freeBarEl.setAttribute('aria-valuenow', String(Math.min(combined, freeThreshold)));
            freeBarEl.setAttribute('aria-valuetext', statusText);
          }

          // Honesty: when the EXISTING cart alone already clears the threshold, say so
          // rather than implying this (possibly empty) set earned the full bar.
          if (freeNoteEl) {
            if (cartCents >= freeThreshold) {
              freeNoteEl.textContent = copy.freeCart;
              freeNoteEl.hidden = false;
            } else {
              freeNoteEl.textContent = '';
              freeNoteEl.hidden = true;
            }
          }

          // Polite announcement only on an actual milestone crossing — never every
          // re-render, never on first paint (freeReachedState starts null).
          if (freeReachedState !== null && freeReachedState !== reached && freeLiveEl) {
            freeLiveEl.textContent = reached
              ? copy.freeAnnounce
              : money(freeThreshold - combined) + ' ' + copy.freeAway;
          }
          freeReachedState = reached;
        }

        function renderButtons() {
          var f = flow();
          var step = state.step;
          if (backBtn) backBtn.hidden = step <= f[0];
          if (nextBtn) {
            nextBtn.hidden = step === STEP_MAX;
            // Step 1's gate is a chosen book (its edition), not just a line.
            nextBtn.disabled = step === STEP_MIN && state.book_variant_id == null;
            // Contextual label ("Next: Cover") tells the shopper where they are
            // headed. The name comes from the next applicable step's own chip, so
            // it tracks the flow (coc is skipped for HON / 5-Year / empty sizes)
            // and any merchant re-labelling of the chips.
            var nextLabel = nextBaseLabel;
            if (step !== STEP_MAX) {
              var chip = indicators[String(nextStep())];
              var chipLabel = chip && chip.querySelector('.hobonichi-builder-app__step-chip-label');
              var chipText = chipLabel && chipLabel.textContent ? chipLabel.textContent.trim() : '';
              if (chipText) nextLabel = nextBaseLabel + ': ' + chipText;
            }
            if (nextBtn.textContent !== nextLabel) nextBtn.textContent = nextLabel;
          }
          if (addAllBtn) {
            addAllBtn.hidden = step !== STEP_MAX;
            addAllBtn.disabled = submitting || state.book_variant_id == null || hasSoldOutSelection();
          }
          // Skip shows only on the optional steps (Cover / Cover on cover / Extras) —
          // never Book or Review. Its role + descriptive accessible label are set per
          // step; the visible text stays the compact "Skip" beside Next.
          if (navSkipBtn) {
            var skipRole = step === 2 ? 'cover' : step === 3 ? 'coc' : step === 4 ? 'extra' : '';
            var showSkip = !!skipRole && f.indexOf(step) !== -1 && step !== STEP_MAX;
            navSkipBtn.hidden = !showSkip;
            if (showSkip) {
              navSkipBtn.setAttribute('data-skip-role', skipRole);
              navSkipBtn.setAttribute(
                'aria-label',
                skipRole === 'cover' ? copy.skipCover : skipRole === 'coc' ? copy.skipCoc : copy.skipExtra
              );
            }
          }
        }

        function render() {
          var step = state.step;

          Object.keys(panels).forEach(function (n) {
            panels[n].hidden = parseInt(n, 10) !== step;
          });

          var f = flow();
          Object.keys(indicators).forEach(function (n) {
            var num = parseInt(n, 10);
            var node = indicators[n];
            var isCurrent = num === step;
            var inFlow = f.indexOf(num) !== -1;
            // "done" now covers any step already reached (behind current, or
            // ahead via a remembered maxStepReached after a chip-jump back) —
            // for the common case (no back-jump yet) maxStepReached === step,
            // so this reduces to exactly the old `num < step`.
            var reached = inFlow && !isCurrent && num <= state.maxStepReached;
            var reachable = isStepReachable(state, num, f);
            node.classList.toggle('hobonichi-builder-app__step-chip--active', isCurrent);
            node.classList.toggle('hobonichi-builder-app__step-chip--done', reached);
            node.classList.toggle('hobonichi-builder-app__step-chip--skipped', !inFlow);
            var trigger = node.querySelector('[data-builder-step-goto]');
            if (trigger) {
              trigger.disabled = !reachable;
              if (isCurrent) trigger.setAttribute('aria-current', 'step');
              else trigger.removeAttribute('aria-current');
              if (reachable) {
                var chipLabelEl = trigger.querySelector('.hobonichi-builder-app__step-chip-label');
                var chipLabelText = chipLabelEl && chipLabelEl.textContent ? chipLabelEl.textContent.trim() : '';
                trigger.setAttribute('aria-label', 'Back to step ' + num + (chipLabelText ? ': ' + chipLabelText : ''));
              } else {
                trigger.removeAttribute('aria-label');
              }
            }
          });

          var pos = f.indexOf(step);
          if (pos === -1) pos = 0;
          // Denominator stability (D3): before a book is chosen show the maximal
          // flow length, so the count only ever DECREASES as skips resolve (reads
          // as progress, never inflation). Once a book is picked, use its real flow.
          var denom = state.book_variant_id == null ? STEP_MAX - STEP_MIN + 1 : f.length;
          var stepLabel = 'Step ' + (pos + 1) + ' of ' + denom;
          if (progressText) progressText.textContent = stepLabel;
          // Per-step eyebrow ("STEP 4 OF 5", uppercased in CSS) — the current panel
          // is always the current step, so its eyebrow tracks the live flow length.
          var currentPanel = panels[String(step)];
          if (currentPanel) {
            var stepEyebrow = currentPanel.querySelector('[data-builder-step-eyebrow]');
            if (stepEyebrow) stepEyebrow.textContent = stepLabel;
          }

          lineButtons.forEach(function (btn) {
            var selected = btn.getAttribute('data-builder-line') === state.line;
            btn.classList.toggle('hobonichi-builder-app__line--selected', selected);
            btn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });

          renderEditions();
          renderCoverGrids();
          renderCocGrids();
          renderExtrasGrids();
          renderCardSelection();
          renderReview();
          renderSp();
          renderTotal();
          renderFreeShip();
          renderButtons();
        }

        // Move focus to the current step's heading — only on an actual step
        // transition (never on a same-step selection, which would otherwise
        // yank the viewport back up to the heading as you pick covers/extras).
        function focusStepHeading() {
          var panel = panels[String(state.step)];
          var heading = panel && panel.querySelector('[data-builder-step-heading]');
          if (heading) heading.focus();
        }

        function dispatch(name) {
          try {
            root.dispatchEvent(
              new CustomEvent('hobonichi-builder:' + name, {
                bubbles: true,
                detail: { state: cloneState(state), pendingBook: pendingBook }
              })
            );
          } catch (error) {
            /* CustomEvent unsupported — the UI still works */
          }
        }

        function commit(next, opts) {
          opts = opts || {};
          var stepChanged = next.step !== state.step;
          state = next;
          persist(state);
          if (opts.push) {
            try {
              window.history.pushState({ hobonichiBuilderStep: state.step }, '');
            } catch (error) {
              /* history unavailable — navigation still works */
            }
          }
          render();
          if (stepChanged) focusStepHeading();
          // Landing on Review: re-read the live cart so the free-shipping bar reflects
          // any cart change made earlier in the session before it comes into view.
          if (stepChanged && state.step === STEP_MAX) refreshLiveCart();
          dispatch('change');
          if (stepChanged) {
            dispatch('step');
            track('builder_step', { step: state.step });
          }
          maybeLoadUniversalExtras();
        }

        function goToStep(n, opts) {
          commit(withStep(state, snapStep(clampStep(n))), opts || { push: true });
        }

        // ---- selection handlers --------------------------------------------

        function pickBook(card) {
          var id = card.getAttribute('data-variant-id');
          if (card.getAttribute('data-available') !== 'true' || !id) return;
          var line = card.getAttribute('data-line');
          var meta = {
            handle: card.getAttribute('data-handle') || '',
            title: card.getAttribute('data-title') || '',
            price: parseInt(card.getAttribute('data-price'), 10) || 0,
            image: card.getAttribute('data-image') || '',
            line: line || '',
            size: card.getAttribute('data-size') || ''
          };
          var next = state;
          if (line && next.line !== line) next = withLine(next, line);
          next = withBook(next, id, meta);
          next = withStep(next, firstStepAfterBook(next));
          commit(next, { push: true });
        }

        function pickCover(card) {
          var id = card.getAttribute('data-variant-id');
          if (card.getAttribute('data-available') !== 'true' || !id) return;
          var nextId = String(state.cover_id) === id ? null : id;
          commit(withCover(state, nextId), { push: false });
        }

        function pickCoc(card) {
          var id = card.getAttribute('data-variant-id');
          if (card.getAttribute('data-available') !== 'true' || !id) return;
          var nextId = String(state.coc_id) === id ? null : id;
          commit(withCoc(state, nextId), { push: false });
        }

        function pickExtra(card) {
          var id = card.getAttribute('data-variant-id');
          if (card.getAttribute('data-available') !== 'true' || !id) return;
          commit(toggleExtra(state, id), { push: false });
        }

        function handlePick(card) {
          switch (card.getAttribute('data-role')) {
            case 'book': pickBook(card); break;
            case 'cover': pickCover(card); break;
            case 'coc': pickCoc(card); break;
            case 'extra': pickExtra(card); break;
          }
        }

        function handleSkip(role) {
          var next = state;
          if (role === 'cover') next = withCover(next, null);
          else if (role === 'coc') next = withCoc(next, null);
          commit(withStep(next, nextStep()), { push: true });
        }

        function handleLine(btn) {
          var key = btn.getAttribute('data-builder-line');
          // Selecting a line filters the edition picker and stays on step 1;
          // choosing a book is what advances to step 2.
          commit(withLine(state, key), { push: false });
          // The edition picker sits below the seven line cards, so on a phone the
          // books that just appeared are a screen or more down. Bring the region
          // into view and land focus in it (its aria-live note carries the
          // spoken update) so sighted, keyboard and AT users all see the reveal.
          if (state.line === key && editionsMount) {
            revealRegion(editionsMount, editionsMount);
          }
        }

        // ---- add all to cart ------------------------------------------------

        function setAddBusy(busy) {
          if (!addAllBtn) return;
          addAllBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
          addAllBtn.classList.toggle('hb-addall--busy', busy);
          // The CTA reads "Checkout"; while the add + SP-gated checkout submit are
          // in flight it reads "Preparing checkout" (the visible text is masked to
          // a spinner by CSS, but the accessible name stays truthful). Stash the
          // merchant label once so it restores exactly on the non-navigating paths.
          var label = addAllBtn.querySelector('[data-addall-label]');
          if (!label) return;
          if (busy) {
            if (label.getAttribute('data-cta-label') == null) {
              label.setAttribute('data-cta-label', label.textContent);
            }
            label.textContent = copy.adding;
          } else {
            var cta = label.getAttribute('data-cta-label');
            if (cta != null) {
              label.textContent = cta;
              label.removeAttribute('data-cta-label');
            }
          }
        }

        function showAddError(message) {
          if (!addErrorEl) return;
          addErrorEl.textContent = message || copy.error;
          addErrorEl.hidden = false;
          // The error sits at the foot of the review panel; on a long set that
          // can be below the fold when the sticky Add-all is tapped. Bring it
          // into view (its role=alert / aria-live already announces it to AT).
          try {
            addErrorEl.scrollIntoView({
              block: 'center',
              behavior: prefersReducedMotion() ? 'auto' : 'smooth'
            });
          } catch (error) {
            /* older browsers: the message is shown, just not auto-scrolled */
          }
        }

        function clearAddError() {
          if (!addErrorEl) return;
          addErrorEl.textContent = '';
          addErrorEl.hidden = true;
        }

        // Per-item outcome report for a partial add (D1). Names each item and, for
        // failures, a human reason mapped from Shopify's error; the items that DID
        // add are marked added. Lives in the review panel; role=status/aria-live
        // announces it. Everything written via textContent.
        function renderAddReport(outcomes) {
          if (!addReportEl) return;
          addReportEl.textContent = '';
          var heading = el('p', 'hb-review__report-heading');
          heading.textContent = copy.partialHeading;
          addReportEl.appendChild(heading);
          var list = el('ul', 'hb-review__report-list');
          list.setAttribute('role', 'list');
          outcomes.forEach(function (o) {
            var item = el('li', 'hb-review__report-item ' + (o.ok ? 'is-added' : 'is-failed'));
            var name = el('span', 'hb-review__report-name');
            name.textContent = (o.entry && o.entry.title) || '';
            item.appendChild(name);
            var status = el('span', 'hb-review__report-status');
            status.textContent = o.ok ? copy.itemAdded : o.reason;
            item.appendChild(status);
            list.appendChild(item);
          });
          addReportEl.appendChild(list);
          // If ANYTHING was added, offer to proceed with what's in the cart — the
          // same SP-gated checkout submit as the full-success path. There is no
          // auto-redirect here; the shopper chooses this, or fixes + retries. The
          // items that failed simply stay out of the cart.
          if (outcomes.some(function (o) { return o.ok; })) {
            var actions = el('div', 'hb-review__report-actions');
            var proceed = el('button', 'button hb-review__report-checkout');
            proceed.type = 'button';
            proceed.textContent = copy.reportCheckout;
            proceed.setAttribute('data-builder-report-checkout', '');
            proceed.addEventListener('click', function () { submitCheckout(); });
            actions.appendChild(proceed);
            addReportEl.appendChild(actions);
          }
          addReportEl.hidden = false;
          try {
            addReportEl.scrollIntoView({
              block: 'center',
              behavior: prefersReducedMotion() ? 'auto' : 'smooth'
            });
          } catch (error) {
            /* older browsers: the report is shown, just not auto-scrolled */
          }
        }

        function clearAddReport() {
          if (!addReportEl) return;
          addReportEl.textContent = '';
          addReportEl.hidden = true;
        }

        function cartSectionIds() {
          var ids = [];
          document.querySelectorAll('cart-items-component').forEach(function (node) {
            if (node.dataset && node.dataset.sectionId) ids.push(node.dataset.sectionId);
          });
          return ids;
        }

        // Re-check each selected item's live availability after a rejected add,
        // mark whatever sold out mid-flow, and keep the shopper on review.
        function recheckAvailability() {
          var ids = selectedIds();
          var seen = {};
          ids.forEach(function (id) {
            var entry = lookup(id);
            if (!entry || !entry.handle || seen[entry.handle]) return;
            seen[entry.handle] = true;
            fetch('/products/' + entry.handle + '.js', { headers: { Accept: 'application/json' } })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (product) {
                if (!product || !product.variants) return;
                var changed = false;
                product.variants.forEach(function (variant) {
                  var vid = String(variant.id);
                  if (cardIndex[vid] && cardIndex[vid].available !== !!variant.available) {
                    cardIndex[vid].available = !!variant.available;
                    var card = root.querySelector('[data-builder-card][data-variant-id="' + vid + '"]');
                    if (card) {
                      card.setAttribute('data-available', variant.available ? 'true' : 'false');
                      card.classList.toggle('hb-card--soldout', !variant.available);
                      var pick = card.querySelector('[data-builder-pick]');
                      if (pick) pick.disabled = !variant.available;
                    }
                    changed = true;
                  }
                });
                if (changed) render();
              })
              .catch(function () { /* ignore — the error message already showed */ });
          });
        }

        function dispatchCartUpdate(cart, itemCount) {
          // Mirror the theme's CartAddEvent shape (events.js): type 'cart:update',
          // detail.data.source anything but 'shipping-protection' so the drawer
          // auto-opens, detail.data.sections so cart-items-component re-renders.
          try {
            root.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: {},
                  sourceId: 'hobonichi-builder',
                  data: {
                    source: 'hobonichi-builder',
                    itemCount: itemCount,
                    sections: cart && cart.sections
                  }
                }
              })
            );
          } catch (error) {
            /* CustomEvent unsupported — the cart still updated server-side */
          }
        }

        // Map a rejected item's Shopify error to a short, human reason.
        function itemFailureReason(data) {
          var desc = (data && (data.description || data.message)) || '';
          var low = desc.toLowerCase();
          if (low.indexOf('maximum quantity') !== -1 || low.indexOf('in your cart') !== -1) {
            return copy.itemMax;
          }
          if (
            low.indexOf('sold out') !== -1 ||
            low.indexOf('out of stock') !== -1 ||
            low.indexOf('not available') !== -1 ||
            low.indexOf('unavailable') !== -1 ||
            low.indexOf("can't add") !== -1 ||
            low.indexOf('cannot add') !== -1
          ) {
            return soldoutLabel;
          }
          return desc || copy.error;
        }

        // Fallback for a rejected batch: add each item on its own, in bundle order,
        // collecting per-item outcomes and the freshest rendered `sections` from the
        // last item that succeeded (for the drawer morph). Never rejects — a per-item
        // network error is recorded as a failure so the whole set is always reported.
        function addSequentially(ids, url, sectionsParam) {
          var outcomes = [];
          var lastSections = null;
          var i = 0;
          function next() {
            if (i >= ids.length) return Promise.resolve();
            var id = ids[i++];
            var body = { id: Number(id), quantity: 1 };
            if (sectionsParam) body.sections = sectionsParam;
            return fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body)
            })
              .then(function (response) {
                return response.json().then(function (data) {
                  return { ok: response.ok, data: data };
                });
              })
              .then(
                function (res) {
                  var entry = lookup(id);
                  if (res.ok && !(res.data && res.data.status)) {
                    outcomes.push({ id: id, entry: entry, ok: true });
                    if (res.data && res.data.sections) lastSections = res.data.sections;
                  } else {
                    outcomes.push({ id: id, entry: entry, ok: false, reason: itemFailureReason(res.data) });
                  }
                  return next();
                },
                function () {
                  outcomes.push({ id: id, entry: lookup(id), ok: false, reason: copy.error });
                  return next();
                }
              );
          }
          return next().then(function () {
            return { outcomes: outcomes, sections: lastSections };
          });
        }

        // Open the cart drawer once with whatever rendered sections we have
        // (cart-items-component refetches the section itself when none is passed).
        function openCartDrawer(sections, itemCount) {
          dispatchCartUpdate({ sections: sections }, itemCount);
        }

        // Fire the same cart:update the drawer path uses (SP's auto-add + the
        // cart-items morph both listen for it) but WITHOUT letting the drawer
        // auto-open: on the go-straight-to-checkout path the page is about to be
        // replaced, so a drawer flash is pure noise. dispatchEvent is synchronous,
        // so the drawer's CartAddEvent handler runs (and sees no `auto-open`)
        // before we restore the attribute — SP still gets the event it needs.
        function dispatchCartUpdateSilently(sections, itemCount) {
          var drawers = document.querySelectorAll('cart-drawer-component[auto-open]');
          drawers.forEach(function (d) { d.removeAttribute('auto-open'); });
          try {
            dispatchCartUpdate({ sections: sections }, itemCount);
          } finally {
            drawers.forEach(function (d) { d.setAttribute('auto-open', ''); });
          }
        }

        // Proceed to checkout the SAME way the cart page does: build and submit a
        // real Shopify cart POST (form#cart-form + button[name="checkout"], mirroring
        // snippets/cart-products.liquid + snippets/cart-summary.liquid). This is what
        // cart-shipping-protection.js's submit interceptor hooks — it holds the
        // navigation (with its own aria-busy loading state) until the $2.99 SP line
        // has settled. A bare location='/checkout' would BYPASS that gate and race
        // SP's auto-add, so it is deliberately NOT used.
        //
        // NOTE: the interceptor's gate only engages when SP is already tracking a
        // variant (its toggle is in the DOM — i.e. the cart was non-empty, the common
        // case here). From a previously-empty cart the gate may not be open yet; SP's
        // own 1.5s valve tolerates that exactly as it tolerates any un-gated checkout.
        // Params for the money-bearing events, built inside their own guard:
        // nothing on the add-all/checkout path may throw because of analytics
        // arithmetic (submitCheckout latches checkoutSubmitted before anything
        // else runs, so a throw there would dead-end the checkout button).
        function totalParams(extra) {
          var params = extra || {};
          try {
            params.value = reviewTotalCents() / 100;
            params.currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD';
          } catch (error) {
            /* omit value rather than break the builder */
          }
          return params;
        }

        function submitCheckout() {
          if (checkoutSubmitted) return; // one submission only (full-success + report CTA share this)
          checkoutSubmitted = true;
          track('builder_checkout', totalParams({}));
          var form = document.createElement('form');
          form.method = 'post';
          form.action = cartUrl;
          form.id = 'cart-form';
          form.setAttribute('hidden', '');
          form.setAttribute('aria-hidden', 'true');
          var btn = document.createElement('button');
          btn.type = 'submit';
          btn.name = 'checkout';
          form.appendChild(btn);
          document.body.appendChild(form);
          try {
            if (typeof form.requestSubmit === 'function') form.requestSubmit(btn);
            else btn.click();
          } catch (error) {
            // Last-ditch fallbacks; still routes through /cart so SP is not silently dropped.
            try { form.submit(); } catch (ignored) { window.location.href = cartUrl; }
          }
        }

        function announceCheckout() {
          if (addStatusEl) addStatusEl.textContent = copy.added;
        }

        function finishAddSuccess(sections, itemCount) {
          // Full success -> straight to checkout. Keep the button in its preparing
          // state (navigating suppresses the .finally revert), announce the hand-off,
          // dispatch the cart:update SP listens for (drawer suppressed), then submit
          // the SP-gated checkout form.
          navigating = true;
          if (addAllBtn) addAllBtn.disabled = true;
          setAddBusy(true);
          announceCheckout();
          dispatchCartUpdateSilently(sections, itemCount);
          submitCheckout();
        }

        function addAll() {
          if (submitting) return;
          if (state.book_variant_id == null) return;
          if (hasSoldOutSelection()) { showAddError(copy.soldoutRetry); return; }

          var ids = selectedIds();
          if (!ids.length) return;

          track('builder_add_all', totalParams({ items: ids.length }));

          // The double-submit guard is held across the WHOLE sequence (batch and any
          // sequential fallback) and only released in .finally below.
          submitting = true;
          clearAddError();
          clearAddReport();
          setAddBusy(true);
          renderButtons();

          var sectionsParam = cartSectionIds().join(',');
          var url = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';
          var payload = {
            items: ids.map(function (id) { return { id: Number(id), quantity: 1 }; }),
            sections: sectionsParam
          };

          // Fast path: one atomic batch POST. Shopify's items[] endpoint is all-or-
          // nothing, so on ANY non-2xx we fall back to sequential single adds and
          // report per item, rather than surfacing a raw, unattributed 422.
          fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(payload)
          })
            .then(function (response) {
              return response.json().then(function (data) {
                return { ok: response.ok, data: data };
              });
            })
            .then(function (result) {
              if (result.ok && !(result.data && result.data.status)) {
                finishAddSuccess(result.data && result.data.sections, ids.length);
                return;
              }
              // Batch rejected — add each item individually, in bundle order.
              return addSequentially(ids, url, sectionsParam).then(function (seq) {
                var succeeded = seq.outcomes.filter(function (o) { return o.ok; });
                var failed = seq.outcomes.filter(function (o) { return !o.ok; });
                if (failed.length === 0) {
                  // Every item added on the retry — treat as a plain success.
                  finishAddSuccess(seq.sections, succeeded.length);
                  return;
                }
                // Partial (or total) failure: open the drawer for what WAS added
                // (once), then render the per-item report. No success flash.
                if (succeeded.length > 0) openCartDrawer(seq.sections, succeeded.length);
                renderAddReport(seq.outcomes);
                recheckAvailability();
              });
            })
            .catch(function () {
              showAddError(copy.error);
            })
            .finally(function () {
              submitting = false;
              // On the full-success path we're navigating to checkout — leave the
              // button in its disabled "Preparing checkout" state until the page
              // is replaced. Every other outcome restores the idle CTA.
              if (navigating) return;
              setAddBusy(false);
              renderButtons();
            });
        }

        // ---- universal extras completion (past Liquid's 50-product cap) -----
        //
        // Liquid's `collection.products` yields at most 50 items, so the server
        // renders only the first 50 universal extras — whole type groups can be
        // missing. On first entry to step 4 (once, guarded) the remainder are
        // fetched from the storefront's public products.json and merged into the
        // matching type groups, creating new groups + sub-tabs for any type that
        // was absent from the first 50. First paint stays fast (the 50 are already
        // in the DOM); this only tops up what the cap dropped. Every shopper/remote
        // value is written via setAttribute / textContent / img.src — never
        // innerHTML — so the fetched payload can never inject markup.

        var universalExtrasState = 'idle'; // idle -> loading -> done | error

        var OTHER_TYPE_RAW = 'Other Stationery';

        function handleize(value) {
          return String(value == null ? '' : value)
            .toLowerCase()
            .trim()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        }

        var OTHER_TYPE_SLUG = handleize(OTHER_TYPE_RAW);

        // Case-insensitive natural compare, matching the server's `sort_natural`
        // ordering of the type groups.
        function naturalCompare(a, b) {
          return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
        }

        // products.json returns prices as decimal strings (e.g. "14.95"); the
        // server-rendered cards carry data-price in cents. Normalise to cents so
        // the running total + review read merged cards exactly like server ones.
        function priceToCents(price) {
          if (price == null) return 0;
          var n = parseFloat(price);
          if (isNaN(n)) return 0;
          return Math.round(n * 100);
        }

        function withImageWidth(src, width) {
          if (!src) return '';
          return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
        }

        function firstImageUrl(product) {
          var src = '';
          if (product.images && product.images.length && product.images[0]) {
            var first = product.images[0];
            src = typeof first === 'string' ? first : (first.src || '');
          }
          if (!src && product.featured_image) src = product.featured_image;
          return src ? withImageWidth(src, 220) : '';
        }

        function productIsAvailable(product) {
          var vs = product.variants || [];
          for (var i = 0; i < vs.length; i++) {
            if (vs[i] && vs[i].available) return true;
          }
          return false;
        }

        // Mirrors Liquid's `selected_or_first_available_variant | default: first`.
        function chosenVariant(product) {
          var vs = product.variants || [];
          for (var i = 0; i < vs.length; i++) {
            if (vs[i] && vs[i].available) return vs[i];
          }
          return vs[0] || null;
        }

        // The info icon is a <use> reference to the single <symbol id="hb-icon-info">
        // defined once in the section markup (D4 icon dedup). Built fresh here so a
        // lazily-built card never depends on a server-rendered card being present.
        function infoIconRef() {
          var NS = 'http://www.w3.org/2000/svg';
          var svg = document.createElementNS(NS, 'svg');
          svg.setAttribute('viewBox', '0 0 20 20');
          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');
          var use = document.createElementNS(NS, 'use');
          use.setAttribute('href', '#hb-icon-info');
          svg.appendChild(use);
          return svg;
        }

        // Build a card DOM-identical to the server-rendered extras cards.
        function buildExtraCard(product) {
          var variant = chosenVariant(product);
          var variantId = variant && variant.id != null ? String(variant.id) : '';
          if (!variantId) return null;
          var available = productIsAvailable(product);
          var priceCents = variant ? priceToCents(variant.price) : 0;
          var priceText = money(priceCents);
          var title = product.title || '';
          var handle = product.handle || '';
          var url = handle ? '/products/' + handle : '#';
          var imgUrl = firstImageUrl(product);

          var li = document.createElement('li');
          li.className = 'hb-card' + (available ? '' : ' hb-card--soldout');
          li.setAttribute('data-builder-card', '');
          li.setAttribute('data-role', 'extra');
          li.setAttribute('data-variant-id', variantId);
          li.setAttribute('data-handle', handle);
          li.setAttribute('data-price', String(priceCents));
          li.setAttribute('data-price-text', priceText);
          li.setAttribute('data-title', title);
          li.setAttribute('data-image', imgUrl);
          li.setAttribute('data-available', available ? 'true' : 'false');

          var pick = document.createElement('button');
          pick.type = 'button';
          pick.className = 'hb-card__pick';
          pick.setAttribute('data-builder-pick', '');
          pick.setAttribute('aria-pressed', 'false');
          if (!available) {
            pick.disabled = true;
            pick.setAttribute('aria-disabled', 'true');
          }

          var media = el('span', 'hb-card__media');
          if (imgUrl) {
            var img = document.createElement('img');
            img.className = 'hb-card__img';
            img.src = imgUrl;
            img.alt = '';
            img.loading = 'lazy';
            img.width = 220;
            img.height = 275;
            media.appendChild(img);
          } else {
            var placeholder = el('span', 'hb-card__img hb-card__img--placeholder');
            placeholder.setAttribute('aria-hidden', 'true');
            media.appendChild(placeholder);
          }
          if (!available) {
            var flag = el('span', 'hb-card__flag');
            flag.textContent = soldoutLabel;
            media.appendChild(flag);
          }
          pick.appendChild(media);

          var titleEl = el('span', 'hb-card__title');
          titleEl.textContent = title;
          pick.appendChild(titleEl);

          var priceEl = el('span', 'hb-card__price');
          priceEl.textContent = priceText;
          pick.appendChild(priceEl);
          li.appendChild(pick);

          var info = document.createElement('a');
          info.className = 'hb-card__info';
          info.href = url;
          info.target = '_blank';
          info.rel = 'noopener';
          info.setAttribute('data-builder-info', '');
          var iconWrap = el('span', 'hb-card__info-icon');
          iconWrap.setAttribute('aria-hidden', 'true');
          iconWrap.appendChild(infoIconRef());
          info.appendChild(iconWrap);
          var vis = el('span', 'visually-hidden');
          vis.textContent = infoLabel + ' ' + title + ' (' + newtabLabel + ')';
          info.appendChild(vis);
          li.appendChild(info);

          return {
            node: li,
            id: variantId,
            title: title,
            priceCents: priceCents,
            priceText: priceText,
            image: imgUrl,
            handle: handle,
            available: available
          };
        }

        function groupLabelFor(typeRaw, isOther) {
          return isOther ? 'Other stationery' : typeRaw;
        }

        // Read a group's display label back from its heading's leading text node
        // (the "· N" count lives in a trailing span).
        function groupLabelOfElement(groupEl) {
          var head = groupEl.querySelector('.hb-subhead--group');
          if (!head) return '';
          var first = head.firstChild;
          if (first && first.nodeType === 3) return first.textContent.trim();
          return (head.textContent || '').replace(/·.*$/, '').trim();
        }

        function updateGroupCount(groupEl) {
          var countEl = groupEl.querySelector('.hb-subhead__count');
          if (!countEl) return;
          countEl.textContent = '· ' + groupEl.querySelectorAll('[data-builder-card]').length;
        }

        // Insert newEl among container's matched children at its natural-alphabetical
        // slot, always before the "Other stationery" entry (which stays last).
        function insertAlphabetically(container, newEl, label, isOther, selector, labelGetter, isOtherGetter) {
          if (isOther) {
            container.appendChild(newEl);
            return;
          }
          var kids = Array.prototype.slice.call(container.querySelectorAll(selector));
          var otherEl = null;
          var ref = null;
          for (var i = 0; i < kids.length; i++) {
            var k = kids[i];
            if (isOtherGetter(k)) {
              otherEl = k;
              continue;
            }
            if (naturalCompare(label, labelGetter(k)) < 0) {
              ref = k;
              break;
            }
          }
          var before = ref || otherEl || null;
          if (before) container.insertBefore(newEl, before);
          else container.appendChild(newEl);
        }

        function createGroupElement(typeRaw, slug, isOther) {
          var groupEl = el('div', 'hb-extras-group');
          groupEl.setAttribute('data-extras-group', slug);
          groupEl.setAttribute('tabindex', '-1');
          var head = el('h4', 'hb-subhead hb-subhead--group');
          head.appendChild(document.createTextNode(groupLabelFor(typeRaw, isOther) + ' '));
          var count = el('span', 'hb-subhead__count');
          count.textContent = '· 0';
          head.appendChild(count);
          groupEl.appendChild(head);
          var ul = el('ul', 'hb-grid');
          ul.setAttribute('role', 'list');
          groupEl.appendChild(ul);
          return groupEl;
        }

        // Clone a server-rendered sub-tab (keeps the caret SVG) and re-point it at
        // the new group. If the server rendered no sub-tab bar (single group), the
        // groups still render and stay reachable by scroll — we just skip the tab.
        function createSubTab(region, typeRaw, slug, isOther) {
          var row = region.querySelector('.hb-extras-subtabs__row');
          var template = region.querySelector('.hb-extras-subtabs__tab');
          if (!row || !template) return;
          var label = groupLabelFor(typeRaw, isOther);
          var btn = template.cloneNode(true);
          btn.setAttribute('data-builder-extras-group-tab', slug);
          var lbl = btn.querySelector('.hb-extras-subtabs__tab-label');
          if (lbl) lbl.textContent = label;
          insertAlphabetically(
            row,
            btn,
            label,
            isOther,
            '.hb-extras-subtabs__tab',
            function (t) {
              var l = t.querySelector('.hb-extras-subtabs__tab-label');
              return l ? l.textContent.trim() : '';
            },
            function (t) {
              return t.getAttribute('data-builder-extras-group-tab') === OTHER_TYPE_SLUG;
            }
          );
        }

        function showExtrasLoading(region) {
          hideExtrasLoading(region);
          var hint = el('p', 'hb-extras-loading');
          hint.setAttribute('data-extras-loading', '');
          hint.setAttribute('aria-live', 'polite');
          hint.textContent = extrasLoadingCopy;
          region.appendChild(hint);
        }

        function hideExtrasLoading(region) {
          var hint = region.querySelector('[data-extras-loading]');
          if (hint && hint.parentNode) hint.parentNode.removeChild(hint);
        }

        function showExtrasError(region) {
          if (region.querySelector('[data-extras-error]')) return;
          var note = el('p', 'hb-extras-error');
          note.setAttribute('data-extras-error', '');
          note.setAttribute('role', 'status');
          note.textContent = extrasErrorCopy;
          region.appendChild(note);
        }

        // GET /collections/<handle>/products.json?limit=250&page=N, following
        // pagination generically (the universal set is well under one page today,
        // but this stays correct if it grows past 250).
        function fetchAllUniversalProducts(handle) {
          var LIMIT = 250;
          var MAX_PAGES = 20;
          var all = [];
          function page(n) {
            var url = '/collections/' + encodeURIComponent(handle) + '/products.json?limit=' + LIMIT + '&page=' + n;
            return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
              .then(function (response) {
                if (!response.ok) throw new Error('products.json HTTP ' + response.status);
                return response.json();
              })
              .then(function (data) {
                var list = (data && data.products) || [];
                all = all.concat(list);
                if (list.length === LIMIT && n < MAX_PAGES) return page(n + 1);
                return all;
              });
          }
          return page(1);
        }

        function mergeUniversalProducts(region, products) {
          if (!products || !products.length) return 0;

          // Handles already on the page (the server-rendered first 50).
          var rendered = {};
          region.querySelectorAll('[data-builder-card]').forEach(function (card) {
            var h = card.getAttribute('data-handle');
            if (h) rendered[h] = true;
          });

          // Missing products, grouped by their (folded) product type.
          var byType = {};
          var order = [];
          products.forEach(function (product) {
            var handle = product.handle;
            if (!handle || rendered[handle]) return;
            var hasType = product.product_type && String(product.product_type).trim();
            var typeRaw = hasType ? String(product.product_type) : OTHER_TYPE_RAW;
            var isOther = typeRaw === OTHER_TYPE_RAW;
            var slug = handleize(typeRaw);
            if (!byType[slug]) {
              byType[slug] = { typeRaw: isOther ? OTHER_TYPE_RAW : typeRaw, slug: slug, isOther: isOther, items: [] };
              order.push(slug);
            }
            byType[slug].items.push(product);
          });

          var added = 0;
          order.forEach(function (slug) {
            var grp = byType[slug];
            // Available-first inside the group (mirrors the server's avail|concat).
            grp.items.sort(function (a, b) {
              return (productIsAvailable(a) ? 0 : 1) - (productIsAvailable(b) ? 0 : 1);
            });
            var groupEl = region.querySelector('[data-extras-group="' + slug + '"]');
            if (!groupEl) {
              groupEl = createGroupElement(grp.typeRaw, grp.slug, grp.isOther);
              insertAlphabetically(
                region,
                groupEl,
                groupLabelFor(grp.typeRaw, grp.isOther),
                grp.isOther,
                '[data-extras-group]',
                groupLabelOfElement,
                function (g) {
                  return g.getAttribute('data-extras-group') === OTHER_TYPE_SLUG;
                }
              );
              createSubTab(region, grp.typeRaw, grp.slug, grp.isOther);
            }
            var ul = groupEl.querySelector('ul.hb-grid') || groupEl.querySelector('.hb-grid');
            if (!ul) return;
            grp.items.forEach(function (product) {
              var built = buildExtraCard(product);
              if (!built || cardIndex[built.id]) return;
              ul.appendChild(built.node);
              cardIndex[built.id] = {
                role: 'extra',
                title: built.title,
                price: built.priceCents,
                priceText: built.priceText,
                image: built.image,
                handle: built.handle,
                line: '',
                size: '',
                available: built.available
              };
              added++;
            });
          });

          // Refresh every group's heading count to match the cards now present.
          region.querySelectorAll('[data-extras-group]').forEach(updateGroupCount);
          return added;
        }

        function loadUniversalExtras() {
          if (universalExtrasState !== 'idle') return;
          var region = root.querySelector('[data-extras-region="universal"]');
          if (!region) {
            universalExtrasState = 'done';
            return;
          }
          var handle = region.getAttribute('data-universal-collection');
          if (!handle) {
            universalExtrasState = 'done';
            return;
          }
          universalExtrasState = 'loading';
          showExtrasLoading(region);
          fetchAllUniversalProducts(handle)
            .then(function (products) {
              hideExtrasLoading(region);
              mergeUniversalProducts(region, products);
              universalExtrasState = 'done';
              // Re-render so selection / review / total reflect the merged cards
              // (covers a lazily-loaded extra chosen in a prior stored session).
              render();
            })
            .catch(function (error) {
              hideExtrasLoading(region);
              showExtrasError(region);
              universalExtrasState = 'error';
              if (window.console && console.warn) {
                console.warn('[hobonichi-builder] universal extras completion failed:', error);
              }
            });
        }

        function maybeLoadUniversalExtras() {
          if (state.step >= 4) loadUniversalExtras();
        }

        // ---- info modal (quick view) ---------------------------------------
        //
        // A builder-owned native <dialog> opened by the card ⓘ. It reuses the
        // theme's dialog-modal look but none of the quick-add component JS — zero
        // coupling to the shared cart machinery. On open it paints instantly from
        // the card's own data-* (title, price, image, availability), then enriches
        // the gallery + description from a cached /products/{handle}.js fetch. The
        // primary "Add to bundle" CTA runs the same selection the card tap does,
        // with per-step semantics; a quiet "View full details" link stays as a
        // no-JS-parity escape hatch to the PDP.

        var qvDialog = root.querySelector('[data-builder-quickview]');
        var qvTrack = root.querySelector('[data-builder-quickview-frame]');
        var qvNav = root.querySelector('[data-builder-quickview-nav]');
        var qvPrevBtn = root.querySelector('[data-builder-quickview-prev]');
        var qvNextBtn = root.querySelector('[data-builder-quickview-next]');
        var qvCounter = root.querySelector('[data-builder-quickview-counter]');
        var qvEyebrow = root.querySelector('[data-builder-quickview-eyebrow]');
        var qvTitle = root.querySelector('[data-builder-quickview-title]');
        var qvPrice = root.querySelector('[data-builder-quickview-price]');
        var qvDesc = root.querySelector('[data-builder-quickview-desc]');
        var qvDetails = root.querySelector('[data-builder-quickview-details]');
        var qvAddBtn = root.querySelector('[data-builder-quickview-add]');
        var qvViewMore = root.querySelector('[data-builder-quickview-viewmore]');
        var qvCloseBtn = root.querySelector('[data-builder-quickview-close]');

        var qvCopy = {
          add: root.getAttribute('data-copy-qv-add') || 'Add to bundle',
          remove: root.getAttribute('data-copy-qv-remove') || 'Remove from bundle',
          details: root.getAttribute('data-copy-qv-details') || 'View full details'
        };

        var productCache = {}; // handle -> /products/{handle}.js payload (success only)
        var qvCard = null; // the card the modal is currently showing
        var qvTrigger = null; // the ⓘ to return focus to on close
        var qvImages = []; // gallery image URLs (CDN width param applied)
        var qvIndex = 0;
        var qvClosing = false;
        var qvAfterClose = null; // ran once after close cleanup (book -> advance step)
        var qvFocusTrigger = true; // restore focus to the trigger on close?
        var qvPrevScrollY = 0;
        var qvReqId = 0; // guards a stale fetch from painting into a newer/closed modal
        var qvScrollRaf = 0; // rAF handle coalescing scroll -> counter sync
        var qvProgrammatic = false; // true while a button/keyboard/settle scroll runs
        var qvProgTimer = 0; // safety reset for the programmatic flag

        function qvEyebrowFor(role) {
          if (role === 'book') return copy.book;
          if (role === 'cover') return copy.cover;
          if (role === 'coc') return copy.coc;
          if (role === 'extra') return copy.extra;
          return '';
        }

        // Gallery = a horizontal scroll-snap track, the SAME mechanic as the theme's
        // PDP media gallery (assets/slideshow.js over base.css `slideshow-slides`):
        // each image is a full-width snap slide, scrolled natively by touch/trackpad
        // and programmatically by the arrows. The counter follows the SCROLL position
        // (not just button clicks). First image loads eager, the rest lazy; a single
        // image hides the controls; reduced-motion snaps instantly (CSS + behavior).

        function qvUpdateCounter() {
          if (qvCounter) qvCounter.textContent = qvImages.length ? qvIndex + 1 + ' / ' + qvImages.length : '';
        }

        // Non-infinite, like the PDP slideshow's `set current` (prev off at the first
        // slide, next off at the last) — a scroll track has finite bounds.
        function qvUpdateArrows() {
          if (qvPrevBtn) qvPrevBtn.disabled = qvIndex <= 0;
          if (qvNextBtn) qvNextBtn.disabled = qvIndex >= qvImages.length - 1;
        }

        // Nearest slide to the current scroll offset. Slides are full-track-width, so
        // |scrollLeft| / trackWidth rounds to the visible index (abs() is RTL-safe).
        function qvVisibleIndex() {
          if (!qvTrack) return 0;
          var w = qvTrack.clientWidth || 1;
          var i = Math.round(Math.abs(qvTrack.scrollLeft) / w);
          if (i < 0) i = 0;
          if (i > qvImages.length - 1) i = qvImages.length - 1;
          return i;
        }

        function qvSyncFromScroll() {
          var i = qvVisibleIndex();
          if (i === qvIndex) return;
          qvIndex = i;
          qvUpdateCounter();
          qvUpdateArrows();
        }

        // Programmatic navigation (buttons / keyboard / drag-settle). Like the PDP
        // slideshow's Scroller — which flags programmatic scrolls so its sync callback
        // ignores the intermediate frames (assets/slideshow.js #userEvent / #ignore) —
        // we set qvProgrammatic so the scroll listener does NOT re-derive the index
        // from mid-animation positions and clobber a just-committed target. The index
        // is authoritative here; the scroll listener resumes on scrollend.
        function qvScrollToIndex(i) {
          if (!qvTrack || qvImages.length < 2) return;
          if (i < 0) i = 0;
          if (i > qvImages.length - 1) i = qvImages.length - 1;
          qvIndex = i;
          qvUpdateCounter();
          qvUpdateArrows();
          var left = i * qvTrack.clientWidth;
          if (Math.round(qvTrack.scrollLeft) === Math.round(left)) return; // already there
          qvProgrammatic = true;
          if (qvProgTimer) clearTimeout(qvProgTimer);
          // Safety reset in case scrollend never fires (e.g. a scroll that stalls).
          qvProgTimer = window.setTimeout(function () {
            qvProgrammatic = false;
            qvProgTimer = 0;
          }, 700);
          try {
            qvTrack.scrollTo({ left: left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          } catch (error) {
            qvTrack.scrollLeft = left; // older browsers: no options-object scrollTo
          }
        }

        function qvBuildSlides() {
          if (!qvTrack) return;
          while (qvTrack.firstChild) qvTrack.removeChild(qvTrack.firstChild);
          for (var i = 0; i < qvImages.length; i++) {
            var slide = document.createElement('div');
            slide.className = 'hb-quickview__slide';
            var img = document.createElement('img');
            img.className = 'hb-quickview__img';
            img.decoding = 'async';
            img.loading = i === 0 ? 'eager' : 'lazy';
            img.alt = ''; // decorative — the modal title already names the product
            img.draggable = false; // so a mouse click-drag never starts a native image drag
            img.src = qvImages[i];
            slide.appendChild(img);
            qvTrack.appendChild(slide);
          }
        }

        function qvSetImages(images) {
          qvImages = images && images.length ? images : [];
          qvIndex = 0;
          qvBuildSlides();
          if (qvTrack) {
            qvTrack.scrollLeft = 0;
            if (qvImages.length >= 2) qvTrack.setAttribute('tabindex', '0');
            else qvTrack.removeAttribute('tabindex');
            qvTrack.setAttribute('aria-label', ((qvTitle && qvTitle.textContent) || 'Product') + ' images');
          }
          if (qvNav) qvNav.hidden = qvImages.length < 2;
          qvUpdateCounter();
          qvUpdateArrows();
        }

        // The CTA label + state reflect the card's role, availability and (for
        // extras) whether it is already in the bundle.
        function syncQuickviewAdd() {
          if (!qvAddBtn || !qvCard) return;
          var role = qvCard.getAttribute('data-role') || '';
          var id = qvCard.getAttribute('data-variant-id');
          var available = qvCard.getAttribute('data-available') === 'true';
          if (!available) {
            qvAddBtn.textContent = soldoutLabel;
            qvAddBtn.disabled = true;
            qvAddBtn.removeAttribute('aria-pressed');
            return;
          }
          qvAddBtn.disabled = false;
          if (role === 'extra') {
            var selected = state.extras.map(String).indexOf(String(id)) !== -1;
            qvAddBtn.textContent = selected ? qvCopy.remove : qvCopy.add;
            qvAddBtn.setAttribute('aria-pressed', selected ? 'true' : 'false');
          } else {
            qvAddBtn.textContent = qvCopy.add;
            qvAddBtn.removeAttribute('aria-pressed');
          }
        }

        function qvProductImages(product) {
          var out = [];
          var list = (product && product.images) || [];
          for (var i = 0; i < list.length; i++) {
            var item = list[i];
            var src = typeof item === 'string' ? item : (item && item.src) || '';
            if (src) out.push(withImageWidth(src, 640));
          }
          if (!out.length && product && product.featured_image) {
            out.push(withImageWidth(product.featured_image, 640));
          }
          return out;
        }

        // Render the merchant-authored product description. body_html is same-trust
        // store content (the theme renders product descriptions as HTML everywhere).
        // DOMParser+importNode blocks <script> execution but NOT event-handler
        // attributes; acceptable only because descriptions are merchant-authored.
        // If this ever renders user-supplied content, sanitize first.
        function qvRenderDescription(html) {
          if (!qvDesc) return;
          while (qvDesc.firstChild) qvDesc.removeChild(qvDesc.firstChild);
          if (!html) return;
          try {
            var parsed = new DOMParser().parseFromString(String(html), 'text/html');
            var body = parsed && parsed.body;
            if (!body) return;
            var imported = document.importNode(body, true);
            while (imported.firstChild) qvDesc.appendChild(imported.firstChild);
          } catch (error) {
            /* leave the description empty rather than surface raw markup */
          }
        }

        function qvApplyProduct(product, reqId) {
          if (reqId !== qvReqId || !product) return; // superseded by a newer open/close
          var imgs = qvProductImages(product);
          if (imgs.length) qvSetImages(imgs);
          qvRenderDescription(product.description || product.body_html || '');
        }

        function qvLoadProduct(handle, reqId) {
          if (!handle) return;
          if (productCache[handle]) {
            qvApplyProduct(productCache[handle], reqId);
            return;
          }
          fetch('/products/' + encodeURIComponent(handle) + '.js', {
            headers: { Accept: 'application/json' },
            credentials: 'same-origin'
          })
            .then(function (r) {
              return r.ok ? r.json() : null;
            })
            .then(function (product) {
              if (product) productCache[handle] = product;
              qvApplyProduct(product, reqId);
            })
            .catch(function () {
              /* graceful degrade — card title/price/image + the PDP link already show */
            });
        }

        function openQuickview(card, trigger) {
          if (!qvDialog || !card) return;
          qvCard = card;
          qvTrigger = trigger || null;
          qvFocusTrigger = true;
          qvAfterClose = null;
          var reqId = ++qvReqId;

          var role = card.getAttribute('data-role') || '';
          var handle = card.getAttribute('data-handle') || '';
          var title = card.getAttribute('data-title') || '';
          var priceText = card.getAttribute('data-price-text') || '';
          var image = card.getAttribute('data-image') || '';

          // Instant paint from the card's own data.
          if (qvEyebrow) qvEyebrow.textContent = qvEyebrowFor(role);
          if (qvTitle) qvTitle.textContent = title;
          if (qvPrice) qvPrice.textContent = priceText;
          qvRenderDescription(''); // reset; enriched by the fetch
          if (qvViewMore) {
            qvViewMore.textContent = qvCopy.details;
            var href = handle ? '/products/' + handle : (trigger && trigger.getAttribute('href')) || '#';
            qvViewMore.setAttribute('href', href);
          }
          qvSetImages(image ? [withImageWidth(image, 640)] : []);
          syncQuickviewAdd();

          if (qvDetails) qvDetails.scrollTop = 0;

          qvPrevScrollY = window.scrollY || window.pageYOffset || 0;
          try {
            qvDialog.showModal();
          } catch (error) {
            try {
              qvDialog.show();
            } catch (ignored) {
              /* no dialog support — the anchor's default PDP nav is the fallback */
              return;
            }
          }
          // Scroll-lock via the theme's existing html[scroll-lock] hook (base.css).
          document.documentElement.setAttribute('scroll-lock', '');
          // Land focus on the title (the aria-labelledby target) so the product
          // name is announced; keep the viewport still.
          if (qvTitle && typeof qvTitle.focus === 'function') {
            try {
              qvTitle.focus({ preventScroll: true });
            } catch (error) {
              try {
                qvTitle.focus();
              } catch (ignored) {
                /* no-op */
              }
            }
          }

          qvLoadProduct(handle, reqId);
        }

        // Single funnel for every close path (close button, backdrop, Escape, and
        // the select-and-close CTAs). Plays the theme's exit animation, then calls
        // dialog.close() — whose native 'close' event runs onQuickviewClosed.
        function requestCloseQuickview(opts) {
          opts = opts || {};
          if (!qvDialog || !qvDialog.open || qvClosing) return;
          qvClosing = true;
          if (opts.focusTrigger === false) qvFocusTrigger = false;
          if (opts.afterClose) qvAfterClose = opts.afterClose;

          var finished = false;
          function finish() {
            if (finished) return;
            finished = true;
            qvDialog.removeEventListener('animationend', onEnd);
            qvDialog.classList.remove('dialog-closing');
            qvDialog.style.animation = '';
            try {
              qvDialog.close();
            } catch (error) {
              onQuickviewClosed();
            }
          }
          function onEnd(event) {
            if (event.target === qvDialog) finish();
          }

          if (prefersReducedMotion()) {
            finish();
            return;
          }
          // Restart the animation cleanly (mirrors the theme's dialog.js), then
          // close on animationend, with a timeout fallback so it always resolves.
          qvDialog.style.animation = 'none';
          void qvDialog.offsetWidth;
          qvDialog.classList.add('dialog-closing');
          qvDialog.style.animation = '';
          qvDialog.addEventListener('animationend', onEnd);
          window.setTimeout(finish, 420);
        }

        function onQuickviewClosed() {
          qvClosing = false;
          document.documentElement.removeAttribute('scroll-lock');
          qvReqId++; // invalidate any in-flight fetch so it can't paint post-close
          var trigger = qvTrigger;
          var focusIt = qvFocusTrigger;
          var after = qvAfterClose;
          qvCard = null;
          qvTrigger = null;
          qvAfterClose = null;
          qvFocusTrigger = true;
          if (after) {
            after();
            return;
          }
          if (focusIt && trigger && typeof trigger.focus === 'function') {
            try {
              trigger.focus();
            } catch (error) {
              /* no-op */
            }
          }
        }

        // Primary CTA — the same selection the card tap performs, per step:
        //   book  -> select + advance to step 2 (defer until closed so focus lands
        //            on the step-2 heading, not the ⓘ)
        //   cover -> force-select this cover, then close
        //   coc   -> force-select this cover on cover, then close
        //   extra -> toggle in/out of the bundle; the modal stays open, CTA flips
        function onQuickviewAdd() {
          if (!qvCard) return;
          var role = qvCard.getAttribute('data-role') || '';
          var id = qvCard.getAttribute('data-variant-id');
          if (!id || qvCard.getAttribute('data-available') !== 'true') return;
          if (role === 'book') {
            var bookCard = qvCard;
            requestCloseQuickview({
              focusTrigger: false,
              afterClose: function () {
                pickBook(bookCard);
              }
            });
            return;
          }
          if (role === 'cover') {
            commit(withCover(state, id), { push: false });
            requestCloseQuickview();
            return;
          }
          if (role === 'coc') {
            commit(withCoc(state, id), { push: false });
            requestCloseQuickview();
            return;
          }
          if (role === 'extra') {
            commit(toggleExtra(state, id), { push: false });
            syncQuickviewAdd();
          }
        }

        if (qvDialog) {
          if (qvCloseBtn) {
            qvCloseBtn.addEventListener('click', function () {
              requestCloseQuickview();
            });
          }
          if (qvPrevBtn) {
            qvPrevBtn.addEventListener('click', function () {
              qvScrollToIndex(qvIndex - 1);
            });
          }
          if (qvNextBtn) {
            qvNextBtn.addEventListener('click', function () {
              qvScrollToIndex(qvIndex + 1);
            });
          }
          if (qvTrack) {
            // Counter + arrow state follow the SCROLL position, so a touch/trackpad
            // swipe (not only the arrows) advances them. rAF-coalesced while scrolling,
            // then confirmed on scrollend where supported.
            qvTrack.addEventListener(
              'scroll',
              function () {
                if (qvProgrammatic) return; // ignore the frames of a button/keyboard scroll
                if (qvScrollRaf) return;
                qvScrollRaf = requestAnimationFrame(function () {
                  qvScrollRaf = 0;
                  qvSyncFromScroll();
                });
              },
              { passive: true }
            );
            qvTrack.addEventListener('scrollend', function () {
              qvProgrammatic = false;
              if (qvProgTimer) {
                clearTimeout(qvProgTimer);
                qvProgTimer = 0;
              }
              qvSyncFromScroll();
            });
            // Keyboard arrows while the gallery track is focused.
            qvTrack.addEventListener('keydown', function (event) {
              if (qvImages.length < 2) return;
              if (event.key === 'ArrowRight' || event.key === 'Right') {
                event.preventDefault();
                qvScrollToIndex(qvIndex + 1);
              } else if (event.key === 'ArrowLeft' || event.key === 'Left') {
                event.preventDefault();
                qvScrollToIndex(qvIndex - 1);
              }
            });

            // Mouse click-drag — the theme's PDP slideshow drags with a pointer on
            // top of the native scroll track (assets/slideshow.js #handleMouseDown).
            // Touch and pen keep the browser's native scroll (so this never fights a
            // finger swipe); only mouse is augmented. Like the PDP, the move/up
            // listeners live on DOCUMENT for the duration of the drag (robust when the
            // cursor leaves the track — pointer capture alone is unreliable). Snap is
            // suspended for 1:1 tracking (mirrors Scroller.snap = false), then the
            // strip settles to the nearest slide and CSS snap is restored.
            var qvDrag = null;
            var qvSnapTimer = 0;

            function qvDragMove(event) {
              if (!qvDrag || event.pointerId !== qvDrag.id) return;
              var dx = event.clientX - qvDrag.x;
              if (!qvDrag.moved) {
                if (Math.abs(dx) < 4) return;
                qvDrag.moved = true;
                qvTrack.style.scrollSnapType = 'none';
                qvTrack.classList.add('hb-quickview__frame--dragging');
              }
              qvTrack.scrollLeft = qvDrag.left - dx;
            }

            function qvDragEnd(event) {
              if (!qvDrag || (event && event.pointerId !== qvDrag.id)) return;
              var moved = qvDrag.moved;
              document.removeEventListener('pointermove', qvDragMove);
              document.removeEventListener('pointerup', qvDragEnd);
              document.removeEventListener('pointercancel', qvDragEnd);
              qvTrack.classList.remove('hb-quickview__frame--dragging');
              qvDrag = null;
              if (!moved) return;
              // Swallow the click a drag would otherwise fire, then settle to the
              // nearest slide and restore CSS scroll-snap after the smooth settle.
              qvTrack.addEventListener(
                'click',
                function (e) {
                  e.stopPropagation();
                  e.preventDefault();
                },
                { capture: true, once: true }
              );
              qvScrollToIndex(qvVisibleIndex());
              if (qvSnapTimer) clearTimeout(qvSnapTimer);
              qvSnapTimer = window.setTimeout(function () {
                qvSnapTimer = 0;
                if (qvTrack) qvTrack.style.scrollSnapType = '';
              }, 380);
            }

            qvTrack.addEventListener('pointerdown', function (event) {
              if (event.pointerType !== 'mouse' || event.button !== 0) return;
              if (qvImages.length < 2) return;
              if (qvSnapTimer) {
                clearTimeout(qvSnapTimer);
                qvSnapTimer = 0;
              }
              qvDrag = { x: event.clientX, left: qvTrack.scrollLeft, moved: false, id: event.pointerId };
              document.addEventListener('pointermove', qvDragMove);
              document.addEventListener('pointerup', qvDragEnd);
              document.addEventListener('pointercancel', qvDragEnd);
            });
          }
          if (qvAddBtn) qvAddBtn.addEventListener('click', onQuickviewAdd);
          // Escape: intercept the native cancel so the exit animation can play;
          // the actual close still routes through requestCloseQuickview -> close.
          qvDialog.addEventListener('cancel', function (event) {
            event.preventDefault();
            requestCloseQuickview();
          });
          // Backdrop click — with showModal(), a click on the backdrop targets the
          // dialog element itself (the content is padding-free and fills the box).
          qvDialog.addEventListener('click', function (event) {
            if (event.target === qvDialog) requestCloseQuickview();
          });
          // Single source of truth for cleanup — fires whether closed by our funnel
          // or by any native path.
          qvDialog.addEventListener('close', onQuickviewClosed);
        }

        // ---- pre-seed resolution -------------------------------------------

        function resolvePendingBook() {
          if (!pendingBook) return;
          var card = bookCards.filter(function (c) {
            return c.getAttribute('data-handle') === pendingBook;
          })[0];
          if (!card) return;
          if (card.getAttribute('data-available') === 'true') {
            pickBook(card);
          } else {
            // Sold out — pre-select the line so its editions show, stay on step 1.
            var line = card.getAttribute('data-line');
            if (line && state.line !== line) commit(withLine(state, line), { push: false });
          }
        }

        // ---- wiring ---------------------------------------------------------

        root.addEventListener('click', function (event) {
          var target = event.target;
          if (!(target instanceof Element)) return;

          var review = target.closest('[data-review-change]');
          if (review && root.contains(review)) {
            if (submitting) return;
            goToStep(parseInt(review.getAttribute('data-review-change'), 10));
            return;
          }
          // Step-indicator chips — re-validate reachability here rather than
          // trusting the button's disabled state alone (belt-and-braces
          // against a stale DOM), then navigate exactly like any other
          // goToStep call (history entry, focus move, persistence).
          var stepGoto = target.closest('[data-builder-step-goto]');
          if (stepGoto && root.contains(stepGoto)) {
            if (submitting || stepGoto.disabled) return;
            var gotoStep = parseInt(stepGoto.getAttribute('data-builder-step-goto'), 10);
            if (isStepReachable(state, gotoStep, flow())) goToStep(gotoStep);
            return;
          }
          var skip = target.closest('[data-builder-skip]');
          if (skip && root.contains(skip)) {
            handleSkip(skip.getAttribute('data-skip-role'));
            return;
          }
          // Extras jump tabs — anchor-scroll to a subsection (reuses revealRegion,
          // the same scroll-into-view + focus used for the step-1 editions reveal).
          var extrasTab = target.closest('[data-builder-extras-tab]');
          if (extrasTab && root.contains(extrasTab)) {
            var extrasRegion = root.querySelector(
              '[data-extras-region="' + extrasTab.getAttribute('data-builder-extras-tab') + '"]'
            );
            if (extrasRegion) revealRegion(extrasRegion, extrasRegion);
            return;
          }
          // Second-level extras jump tabs — anchor-scroll to a per-type group
          // inside the universal region (same revealRegion helper as above).
          var groupTab = target.closest('[data-builder-extras-group-tab]');
          if (groupTab && root.contains(groupTab)) {
            var extrasGroup = root.querySelector(
              '[data-extras-group="' + groupTab.getAttribute('data-builder-extras-group-tab') + '"]'
            );
            if (extrasGroup) revealRegion(extrasGroup, extrasGroup);
            return;
          }
          var lineBtn = target.closest('[data-builder-line]');
          if (lineBtn && root.contains(lineBtn)) {
            handleLine(lineBtn);
            return;
          }
          // Card ⓘ affordance — one delegated listener covers server-rendered AND
          // lazy-loaded extras cards. Intercept the anchor (which without JS is a
          // new-tab PDP link) and open the builder-owned info modal instead.
          var info = target.closest('[data-builder-info]');
          if (info && root.contains(info)) {
            event.preventDefault();
            var infoCard = info.closest('[data-builder-card]');
            if (infoCard) openQuickview(infoCard, info);
            return;
          }
          var pick = target.closest('[data-builder-pick]');
          if (pick && root.contains(pick)) {
            var card = pick.closest('[data-builder-card]');
            if (card) handlePick(card);
          }
        });

        lineButtons.forEach(function (btn) { btn.setAttribute('aria-pressed', 'false'); });

        if (nextBtn) {
          nextBtn.addEventListener('click', function () {
            if (nextBtn.disabled) return;
            goToStep(nextStep());
          });
        }
        if (backBtn) {
          backBtn.addEventListener('click', function () {
            goToStep(prevStep());
          });
        }
        if (addAllBtn) {
          addAllBtn.addEventListener('click', addAll);
        }

        // ---- Shipping-protection review toggle (two-way cart sync) ----------
        //
        // The review SP toggle mirrors the mini cart's SP switch. Flipping it always
        // (a) writes the shared 'sp-opted-out' flag and re-renders the review (toggle,
        // SP line, totals) immediately, AND (b) reconciles the LIVE cart's SP line so
        // the drawer beside the review reflects the change at once:
        //   OFF -> if the SP variant is in the cart, remove it; empty/absent = flag-only
        //   ON  -> if the cart has real items and SP is absent, add it; empty = flag-only
        // The cart call + event mirror cart-shipping-protection.js `_update` exactly:
        // POST /cart/update.js with the SP line set + the cart-items sections in one
        // round-trip, then a cart:update carrying source:'shipping-protection' so the
        // drawer morphs, its toggle re-renders to cart truth, and the SP script treats
        // it as its own work. That is loop-safe: the SP script's _onCartUpdate returns
        // early on source==='shipping-protection', and its auto-add paths both respect
        // the flag and check presence — nothing re-adds or re-removes. Our dispatch is
        // tagged hobonichiBuilderSp so our reverse listener (below) skips our own write.
        // Reconciles are serialised (spSyncing + a single queued re-run) so a rapid
        // double-toggle converges to the final flag state with no duplicate SP or error.

        var spSyncing = false;
        var spSyncQueued = false;

        function spVariantIdNum() {
          return Number(spVariantId);
        }

        function spQtyInCart(cart) {
          var idNum = spVariantIdNum();
          var qty = 0;
          (cart && cart.items ? cart.items : []).forEach(function (item) {
            if (item.id === idNum) qty += item.quantity || 0;
          });
          return qty;
        }

        function cartHasRealItems(cart) {
          var idNum = spVariantIdNum();
          return (cart && cart.items ? cart.items : []).some(function (item) {
            return item.id !== idNum;
          });
        }

        // Fire the SAME cart:update cart-shipping-protection.js `_update` fires, so the
        // drawer's cart-items-component morphs and every SP-aware component reacts
        // exactly as it would to a mini-cart untoggle. resource = the cart with the
        // sections blob stripped (matches the SP script); source:'shipping-protection'
        // routes the SP script down its own no-op branch; hobonichiBuilderSp tags it so
        // our reverse listener does not treat our own write as an external change.
        function dispatchSpSync(cart, sections) {
          var count = cart && cart.item_count ? cart.item_count : 0;
          var visibleCount = Math.max(0, count - spQtyInCart(cart));
          try {
            document.dispatchEvent(
              new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                  resource: cart,
                  sourceId: 'hobonichi-builder-sp',
                  data: {
                    source: 'shipping-protection',
                    hobonichiBuilderSp: true,
                    itemCount: visibleCount,
                    sections: sections
                  }
                }
              })
            );
          } catch (error) {
            /* CustomEvent unsupported — the cart is already updated server-side */
          }
        }

        // POST /cart/update.js with the SP line set to `qty` (1 add / 0 remove),
        // requesting the cart-items sections in the same round-trip (drawer morph with
        // no flash), then dispatch. Mirrors cart-shipping-protection.js `_update`.
        function spCartPost(qty) {
          var sectionIds = cartSectionIds();
          var body = 'updates[' + encodeURIComponent(spVariantId) + ']=' + qty;
          if (sectionIds.length) {
            body += '&' + sectionIds.map(function (id) { return 'sections[]=' + encodeURIComponent(id); }).join('&');
          }
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
            body: body,
            credentials: 'same-origin'
          })
            .then(function (res) {
              if (!res.ok) throw new Error('SP cart update failed: ' + res.status);
              return res.json();
            })
            .then(function (payload) {
              var sections = payload.sections || {};
              var cart = payload;
              if (payload.sections) {
                cart = {};
                for (var key in payload) {
                  if (key !== 'sections' && Object.prototype.hasOwnProperty.call(payload, key)) cart[key] = payload[key];
                }
              }
              dispatchSpSync(cart, sections);
            });
        }

        // Reconcile the live cart's SP line to the shared flag. Serialised: a toggle
        // made while a reconcile is in flight queues exactly one re-run, so N rapid
        // flips settle to the final flag state with no duplicate SP line and no error.
        function reconcileSpCart() {
          if (!spVariantId) return; // no resolvable SP variant — flag-only, cart untouched
          if (spSyncing) { spSyncQueued = true; return; }
          spSyncing = true;
          var optedOut = isSpOptedOut();
          fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
            .then(function (res) { return res.ok ? res.json() : null; })
            .then(function (cart) {
              if (!cart) return null;
              var present = spQtyInCart(cart) > 0;
              if (optedOut) {
                // OFF: drop the SP line if present. Empty/absent cart -> nothing to do.
                if (present) return spCartPost(0);
              } else if (!present && cartHasRealItems(cart)) {
                // ON: add SP only alongside real items — never an SP-only cart.
                return spCartPost(1);
              }
              return null;
            })
            .catch(function () {
              /* network hiccup — the flag stays authoritative for the add-all path */
            })
            .finally(function () {
              spSyncing = false;
              if (spSyncQueued) {
                spSyncQueued = false;
                reconcileSpCart();
              }
            });
        }

        if (spToggle) {
          spToggle.addEventListener('change', function () {
            // Optimistic: write the shared flag + re-render the review immediately,
            // then reconcile the live cart line so the drawer follows suit.
            setSpOptedOut(!spToggle.checked);
            render();
            reconcileSpCart();
          });
        }

        // Reverse-direction live sync: when the cart changes elsewhere while the review
        // is open — most importantly the DRAWER's own SP toggle being flipped — re-read
        // the shared flag and re-render so the builder toggle, SP line and totals track
        // cart truth without a reload. Skip our OWN SP dispatch (tagged) and the
        // straight-to-checkout window (navigating) so we never fight our own writes.
        document.addEventListener('cart:update', function (event) {
          var data = event && event.detail && event.detail.data;
          if (data && data.hobonichiBuilderSp) return; // our own SP write — not an external change
          if (navigating) return; // straight-to-checkout window — don't fight our own hand-off
          if (spModule) render(); // SP toggle / line / totals track cart truth
          // An external cart change (drawer edit, another tab) moves the existing-cart
          // total the free-shipping bar counts — re-read /cart.js and repaint the bar.
          refreshLiveCart();
        });

        window.addEventListener('popstate', function (event) {
          var restored = event.state && event.state.hobonichiBuilderStep;
          commit(withStep(state, snapStep(restored || state.step)), { push: false });
        });

        root.hobonichiBuilder = {
          getState: function () { return cloneState(state); },
          goToStep: function (n) { goToStep(n); },
          selectLine: function (key) { commit(withLine(state, key), { push: false }); },
          selectBook: function (variantId, meta) { commit(withBook(state, variantId, meta), { push: false }); },
          selectCover: function (id) { commit(withCover(state, id), { push: false }); },
          selectCoc: function (id) { commit(withCoc(state, id), { push: false }); },
          toggleExtra: function (id) { commit(toggleExtra(state, id), { push: false }); },
          pendingBook: pendingBook
        };

        try {
          window.history.replaceState({ hobonichiBuilderStep: state.step }, '');
        } catch (error) {
          /* ignore */
        }

        // Land on a valid step for the restored/pre-seeded selection, then paint.
        state = withStep(state, snapStep(state.step));
        render();
        // Prime the existing-cart total so the free-shipping bar is accurate the moment
        // the shopper reaches review (and if a restored session lands there directly).
        refreshLiveCart();
        resolvePendingBook();
        dispatch('ready');
        // If a restored/pre-seeded session lands directly on step 4, complete the
        // universal extras now (commit() covers every later transition into it).
        maybeLoadUniversalExtras();
      }

      try {
        document.querySelectorAll('[data-hobonichi-builder][data-phase="launch"]').forEach(init);
      } catch (error) {
        /* defensive: a builder failing to bind must never break the rest of the page */
      }
    })();
  }

  // -----------------------------------------------------------------------
  // Exports
  // -----------------------------------------------------------------------

  if (typeof window !== 'undefined') {
    window.HobonichiBuilder = core;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = core;
  }
})();
